import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowsRotate } from '@fortawesome/free-solid-svg-icons';
import { fetchAuthSession } from 'aws-amplify/auth';
import { api, ApiError } from '../../lib/api/client';
import { config } from '../../lib/config';
import { type CaptureType } from '../../lib/workflows/registry';
import { buildCustomEft, tcnOfDto, FGP } from '../../lib/workflows/eftBuilder';
import { useAniCache } from './useAniCache';
import { EbtsValidation } from './EbtsValidation';
import { ImportEft } from './ImportEft';
import { FaceCapture } from './FaceCapture';
import { fullImage } from '../../lib/eft/aniCache';
import type { BuildResult } from '../../lib/eft/aniCache';
import { useWorkflows } from '../../lib/workflows/useWorkflows';
import { isWorkflowAuthorized } from '../../lib/workflows/authorization';
import type { WorkflowConfig } from '../../lib/workflows/config';
import { workflowDeviceFields } from '../../lib/workflows/deviceFields';
import { rememberable, withDefaults } from '../../lib/workflows/defaults';
import { FieldInput, lookupOptions, isFieldInvalid } from './DemographicsFields';
import { DeviceService, DeviceServiceBlockedError, DeviceServiceInsecureError, DeviceServicePermissionError, type DeviceInfo } from '../../lib/deviceService/client';
import { useDeviceServiceStatus } from '../../lib/deviceService/status';
import { isTwoFingerScanner } from '../../lib/deviceService/twoFinger';
import { permissionHelp } from '../../lib/deviceService/permissionHelp';
import { useAppVersion } from '../../lib/useAppVersion';
import { shouldBlockStart } from '../../lib/staleGate';
import { StaleGate } from './StaleGate';
import { ScannerSeatPrompt } from '../devices/ScannerLicense';
import { combineQuality, meanQuality, qualityBand } from '../../lib/deviceService/quality';
import { useAccount } from '../../lib/api/hooks';
import { DEFAULT_CONSENT_STATEMENT } from '../../lib/consent';
import { useLicenseScanner } from '../../lib/aamva';
import { useUnsavedGuard } from '../../lib/useUnsavedGuard';
import { saveDraft, loadDraft, deleteDraft, type DraftPayload } from './drafts';
import { useSupport } from '../support/SupportWidget';
import { prepareCardImages } from '../../lib/images/cardImage';
import { describePositions, missingPositions, requiredPositions } from '../../lib/workflows/fingerprintPositions';
import { imagesToClear } from '../../lib/workflows/amputations';

/** Workflows a Site Admin must provision before a customer can access them. */
// Gating comes from each workflow's own requiresAuthorization flag — see lib/workflows/authorization.

type Demo = Record<string, string>;
type Step = 'device' | 'demographics' | 'capture' | 'face' | 'review';
const STEPS: readonly Step[] = ['device', 'demographics', 'capture', 'face', 'review'];
const isStep = (s: unknown): s is Step => typeof s === 'string' && (STEPS as readonly string[]).includes(s);

/**
 * Canonical fingerprint of the operator's work, used to tell "unsaved" from "saved".
 *
 * Keys are sorted and blank demographic answers dropped so the value depends only on content, never
 * on insertion order — a draft rehydrated from JSON must produce a byte-identical snapshot to the one
 * captured when it was saved, or the form would look dirty the instant it loaded. The current step is
 * deliberately excluded: moving between tabs isn't a change to the data.
 */
function snapshotOf(demo: Demo, prints: Record<string, string>, ampCodes: Record<string, AmpCode>, docKeys: string[]): string {
  const entries = (o: Record<string, string>, dropBlank = false) =>
    Object.keys(o).sort().filter((k) => !dropBlank || o[k] !== '').map((k) => [k, o[k]]);
  return JSON.stringify([entries(demo, true), entries(prints), entries(ampCodes), [...docKeys].sort()]);
}
/** Snapshot of a workflow with nothing entered — anything else means there's work worth keeping. */
const EMPTY_SNAPSHOT = snapshotOf({}, {}, {}, []);
/** T2_AMP amputation codes: XX = amputated, UP = unable to print. */
type AmpCode = 'XX' | 'UP';

type DevStatus = 'idle' | 'connecting' | 'connected' | 'unavailable' | 'blocked' | 'insecure' | 'permission';
interface DeviceConn {
  status: DevStatus;
  /** When status is 'permission': whether the browser refused it or has not asked yet. */
  permState?: 'prompt' | 'denied' | null;
  svc: DeviceService | null;
  device: DeviceInfo | null;
  connect: () => Promise<void>;
}

/** Shared Device Service connection — auto-connects when `enabled`, reused across steps. */
function useDeviceConnection(enabled: boolean): DeviceConn {
  const [status, setStatus] = useState<DevStatus>('idle');
  // Kept so the help text can say whether the permission was refused or simply never asked for.
  const [permState, setPermState] = useState<'prompt' | 'denied' | null>(null);
  const [svc, setSvc] = useState<DeviceService | null>(null);
  const [device, setDevice] = useState<DeviceInfo | null>(null);
  const busy = useRef(false);
  // The scanner claim is released from unmount and pagehide handlers that cannot read React state, so the
  // live service and whether a scanner was actually initialized are mirrored into refs they can reach.
  const svcRef = useRef<DeviceService | null>(null);
  const claimed = useRef(false);
  const { report } = useDeviceServiceStatus();

  const connect = useCallback(async () => {
    if (busy.current) return;
    busy.current = true;
    setStatus('connecting');
    try {
      const s = await DeviceService.connect();
      svcRef.current = s;
      s.version().then(report).catch(() => {}); // feed the app-wide update check
      const list = await s.listScanners(true);
      setSvc(s);
      if (list[0]) {
        setDevice(list[0]);
        // Release any claim left from a previous connect in this same Device Service process before
        // re-claiming, so a stale handle can't make the init read as busy.
        await s.deinitScanner().catch(() => undefined);
        await s.initScanner(list[0].deviceIndex ?? 0);
        claimed.current = true;
      } else {
        setDevice(null);
      }
      setStatus('connected');
    } catch (e) {
      if (e instanceof DeviceServicePermissionError) setPermState(e.state);
      setStatus(
        e instanceof DeviceServicePermissionError ? 'permission'
          : e instanceof DeviceServiceInsecureError ? 'insecure'
            : e instanceof DeviceServiceBlockedError ? 'blocked'
            : 'unavailable',
      );
    } finally {
      busy.current = false;
    }
  }, []);

  // Release the scanner when the capture flow goes away. Unmount covers in-app navigation; pagehide covers
  // reload and tab close, which never unmount. Without this the scanner stays claimed and the next session
  // finds a Crossmatch unit reporting itself busy.
  useEffect(() => {
    const release = () => {
      if (!claimed.current) return;
      claimed.current = false;
      void svcRef.current?.release();
    };
    // Restored from the back/forward cache after pagehide released the scanner — go back to 'idle' so the
    // auto-connect effect re-claims a device. Only a bfcache restore (`persisted`) needs this; an ordinary
    // load mounts fresh and connects on its own.
    const onShow = (e: PageTransitionEvent) => {
      if (e.persisted) setStatus('idle');
    };
    window.addEventListener('pagehide', release);
    window.addEventListener('pageshow', onShow);
    return () => {
      window.removeEventListener('pagehide', release);
      window.removeEventListener('pageshow', onShow);
      release();
    };
  }, []);

  useEffect(() => {
    if (enabled && status === 'idle') void connect();
  }, [enabled, status, connect]);

  return { status, svc, device, connect, permState };
}

/** What the entitlement precheck answers. */
interface Precheck { ok: boolean; mode?: string; code?: string | null; message?: string; deviceId?: string; status?: number; minVersion?: string; installedVersion?: string | null }

interface Readiness {
  check: Precheck | null;
  checking: boolean;
  /** Connected, a scanner present, and the plan says this workstation may capture. */
  ready: boolean;
  /** Run the precheck again — after a seat is allocated, a plan bought, or the service updated. */
  recheck: () => void;
}

/**
 * Whether this workstation may capture: scanner connected, and entitled by the plan.
 *
 * Lives HERE, beside the connection, rather than inside the scanner step — which is where it used to live,
 * and that was the bug. `DeviceStep` is only mounted while its tab is showing, so the precheck ran only
 * while the operator stood on it. A fresh capture always starts there so nothing looked wrong; but RESUMING
 * A DRAFT calls setStep directly and lands on whichever tab the draft was saved on, so the check never ran,
 * `ready` stayed false, and the stepper refused to open Fingerprints. Visiting Scanner and coming back
 * "fixed" it by finally mounting the component that does the asking.
 *
 * A gate whose answer only exists while you are looking at the gate is not a gate.
 */
function useCaptureReadiness(conn: DeviceConn, captureType: CaptureType | undefined, enabled: boolean): Readiness {
  const { installed: dsVersion } = useDeviceServiceStatus();
  const [check, setCheck] = useState<Precheck | null>(null);
  const [checking, setChecking] = useState(false);
  const [nonce, setNonce] = useState(0);
  const connected = conn.status === 'connected';
  const serial = conn.device?.deviceSerial;
  const deviceId = conn.device?.deviceId;
  const firmware = conn.device?.deviceVersion;

  useEffect(() => {
    let cancelled = false;
    async function run() {
      if (!enabled || !captureType || !connected || !serial) { setCheck(null); return; }
      setChecking(true);
      try {
        const r = await api.post<Precheck>('/capture/precheck', {
          captureType,
          device: {
            serialNumber: serial,
            installationId: String(deviceId ?? ''),
            ...(firmware ? { firmware } : {}),
            ...(dsVersion ? { deviceServiceVersion: dsVersion } : {}),
          },
        });
        if (!cancelled) setCheck(r);
      } catch (e) {
        if (!cancelled) setCheck({ ok: false, message: e instanceof ApiError ? e.message : 'Could not verify your plan' });
      } finally {
        if (!cancelled) setChecking(false);
      }
    }
    void run();
    return () => { cancelled = true; };
  }, [enabled, connected, serial, captureType, nonce, deviceId, firmware, dsVersion]);

  return {
    check,
    checking,
    ready: connected && Boolean(serial) && check?.ok === true,
    recheck: useCallback(() => setNonce((n) => n + 1), []),
  };
}

export function CaptureFlow() {
  const { openSupport } = useSupport();
  // Reported by the Device Service connection; sent with every entitlement check and capture so the
  // server can hold the minimum-version floor itself.
  const { installed: dsVersion } = useDeviceServiceStatus();
  const { type } = useParams<{ type: CaptureType }>();
  const workflows = useWorkflows();
  const wf: WorkflowConfig | undefined = workflows.data?.find((w) => w.captureType === type);
  const isConv = Boolean(type) && String(type).startsWith('conv');
  const isConversion = wf ? wf.tabs.includes('documents') : isConv;

  const [step, setStep] = useState<Step>(() => (type && !String(type).startsWith('conv') ? 'device' : 'demographics'));
  const [demo, setDemo] = useState<Demo>({});
  // Highlight required-missing / invalid demographic fields in red once the user has tried to move on
  // or submit (so the form isn't red on first load, but errors are obvious when they matter).
  const [showDemoErrors, setShowDemoErrors] = useState(false);
  // Set once the operator has left the Demographics step, so its tab can report real validity instead
  // of a positional "done".
  const [demoVisited, setDemoVisited] = useState(false);
  const [prints, setPrints] = useState<Record<string, string>>({});
  const [ampCodes, setAmpCodes] = useState<Record<string, AmpCode>>({});
  /*
   * Advisory NFIQ per position. Deliberately NOT part of snapshotOf: it is measured metadata, not
   * operator input, so a score arriving must never be what makes the form look unsaved.
   */
  const [quality, setQuality] = useState<Record<string, number | null>>({});
  /*
   * Fingerprints are staged on the backend AS THEY ARE CAPTURED, converted to WSQ and thumbnailed
   * there, so the review step has nothing left to upload. Card conversion is excluded — it has no live
   * capture. Type-4 is the record the live-capture workflows put impressions in (see buildCustomEft).
   */
  const fgpOf = useCallback((slot: string) => FGP[slot], []);
  const aniCache = useAniCache({
    enabled: Boolean(type) && !isConv,
    captureType: type,
    prints,
    fgpOf,
    recordType: 4,
  });

  /*
   * Display images for impressions whose `prints` entry is not something a browser can draw.
   *
   * A captured print arrives as PNG and the grid renders it directly. An IMPORTED one is WSQ — the
   * format the EFT must carry and the browser cannot decode — so it is drawn from the thumbnail the
   * server produced instead. Keyed by scanType alongside `prints`, which stays the authoritative
   * payload either way.
   */
  const [printThumbs, setPrintThumbs] = useState<Record<string, { src: string; forData: string; slot: string }>>({});
  /** Base64 PNG of the subject's photograph, when the workflow takes one. */
  const [facePhoto, setFacePhoto] = useState<string | null>(null);
  const [docKeys, setDocKeys] = useState<string[]>([]);
  const [result, setResult] = useState<{ tcn?: string; msg: string; ebts?: string; filename?: string; stored?: boolean } | null>(null);
  /*
   * Null means "not checked" and is deliberately NOT the same as "clean" — a validation that failed to
   * run must not let a submission through as though it had passed.
   */
  const [ebts, setEbts] = useState<BuildResult | null>(null);
  const [ebtsAcknowledged, setEbtsAcknowledged] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [payRequired, setPayRequired] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [dlBusy, setDlBusy] = useState(false);
  const [dlErr, setDlErr] = useState<string | null>(null);
  const [consentAccepted, setConsentAccepted] = useState(false);
  const [consentName, setConsentName] = useState('');
  const [scanMsg, setScanMsg] = useState<string | null>(null);
  // Draft state. `savedSnapshot` is the snapshot as of the last successful save; the form is dirty
  // whenever the live snapshot differs from it.
  const [searchParams] = useSearchParams();
  const draftParam = searchParams.get('draft');
  /*
   * The transaction this capture REPLACES, when the operator arrived from a file that could not be corrected.
   * Carried through to /capture/eft so the rebuild is not metered — the original was already paid for, and
   * the server verifies the reference belongs to this account before waiving the charge.
   */
  const rebuildOfParam = searchParams.get('rebuildOf');
  const [draftId, setDraftId] = useState<string | null>(draftParam);
  const [savedSnapshot, setSavedSnapshot] = useState<string | null>(null);
  const [draftBusy, setDraftBusy] = useState(false);
  const [draftMsg, setDraftMsg] = useState<string | null>(null);
  const [draftErr, setDraftErr] = useState<string | null>(null);
  const [loadingDraft, setLoadingDraft] = useState(Boolean(draftParam));
  const draftLoaded = useRef(false);
  const account = useAccount();
  const convCredit = useQuery({
    queryKey: ['conv-credits'],
    queryFn: () => api.get<{ unitAmount: number | null; currency: string; balance: number }>('/credits/conversion'),
    enabled: isConv,
  });

  // Driver's-license barcode auto-fill — active only on the demographics step. A keyboard-wedge scanner
  // types the license's AAMVA data; we parse it into the demographic fields (mirrors v1).
  useLicenseScanner(
    step === 'demographics',
    ({ patch, fields }) => {
      setDemo((d) => ({ ...d, ...patch }));
      setScanMsg(`Auto-filled ${Object.keys(patch).length} field(s) from the driver's license (${fields} data elements read).`);
    },
    /*
     * The likeliest cause is the scanner stripping the line endings INSIDE the barcode, which looks
     * exactly like a bad scan — so the message names the setting and points at the page that fixes it,
     * rather than leaving the operator to guess with a subject standing in front of them.
     */
    () => setScanMsg(
      'A barcode was detected but could not be read. The scanner most likely needs PDF417 enabled and must '
      + 'transmit the line endings inside the barcode — see Help & support > Barcode scanner setup.',
    ),
    // This workflow's own fields: a coded value is only filled in where the operator has that option.
    wf?.fields,
  );

  // Resume a saved draft: rehydrate every field, the prints, the marks and the step the operator was
  // on, then record the snapshot so the restored form starts out clean.
  useEffect(() => {
    if (!draftParam || draftLoaded.current) return;
    draftLoaded.current = true;
    let cancelled = false;
    void loadDraft(draftParam)
      .then((p) => {
        if (cancelled) return;
        const d = p.demo ?? {};
        const pr = p.prints ?? {};
        const amp = (p.ampCodes ?? {}) as Record<string, AmpCode>;
        const dk = p.docKeys ?? [];
        setDemo(d);
        setPrints(pr);
        setAmpCodes(amp);
        setQuality(p.quality ?? {});
        setDocKeys(dk);
        if (isStep(p.step)) setStep(p.step);
        setSavedSnapshot(snapshotOf(withoutDeviceFields(d), pr, amp, dk));
      })
      .catch((e) => { if (!cancelled) setDraftErr(e instanceof Error ? e.message : 'Could not open that draft'); })
      .finally(() => { if (!cancelled) setLoadingDraft(false); });
    return () => { cancelled = true; };
  }, [draftParam]);

  // Prefill the subject-consent name from the demographics when entering Review (live scan only).
  useEffect(() => {
    if (step === 'review' && !isConversion && !consentName) {
      const full = `${demo['firstName'] ?? ''} ${demo['lastName'] ?? ''}`.trim();
      if (full) setConsentName(full);
    }
  }, [step, isConversion, demo, consentName]);

  /*
   * Read from the workflow rather than a map keyed by capture type, so a newly configured gated workflow
   * is blocked here too. `wf` may not have loaded yet, and blocking on an unknown workflow would flash a
   * "not provisioned" screen at everyone — the server refuses the submission regardless.
   */
  const gatedBlocked = wf ? !isWorkflowAuthorized(account.data, wf) : false;
  // Live-capture workflows need the Device Service; auto-connect unless the workflow is gated off.
  const conn = useDeviceConnection(Boolean(type) && !isConv && !gatedBlocked && !account.isLoading);
  const device = conn.device;
  /*
   * The stepper pills were plain navigation, so an operator could click straight past a scanner that was not
   * connected, was running a Device Service too old to capture, or was not entitled — and only find out
   * after entering a subject's demographics and trying to capture. A step that refuses to be left is not a
   * step anyone should be able to jump over.
   *
   * One source for that answer, running for as long as the flow is open — not only while the scanner tab is
   * on screen. DeviceStep now reads this rather than owning it, so its Continue button and the pills cannot
   * disagree about what "ready" means.
   */
  const readiness = useCaptureReadiness(conn, type, !isConv && !gatedBlocked);
  const deviceReady = readiness.ready;

  /*
   * Memoised because EbtsValidation re-checks whenever it changes: built inline it would be a new
   * object on every render and the check would loop. Only built on the review step, and only for live
   * capture — card conversion produces its EFT on the backend.
   */
  /*
   * True only when a check actually RAN and found problems. A validation that could not run leaves
   * `ebts` null, which must not silently behave like a clean result — that is reported by the panel
   * itself rather than by demanding an acknowledgment for something nobody was told.
   */
  const ebtsBlocking = Boolean(ebts && !ebts.valid && ebts.issues.length > 0);

  const reviewDto = useMemo(
    () => (wf && !isConv && step === 'review' ? buildCustomEft(wf, demo, prints, ampCodes, device, facePhoto) : null),
    [wf, isConv, step, demo, prints, ampCodes, device, facePhoto],
  );


  // Unsaved-work tracking. Must sit above the early returns below so the hook order stays stable.
  /**
   * Demographic fields filled from the connected scanner (SWFT's make / model / serial).
   *
   * Held separately because they are hardware-derived, not operator input: they are excluded from the
   * dirty snapshot below, or simply connecting a scanner on an untouched form would report "unsaved
   * changes" and prompt on navigation. They are still saved in a draft like any other demographic value,
   * and re-seeded on load, so nothing is lost by leaving them out of the comparison.
   */
  const deviceFields = useMemo(() => workflowDeviceFields(wf), [wf]);
  const deviceDemoKeys = useMemo(
    () => Object.values(deviceFields).filter((v): v is string => Boolean(v)),
    [deviceFields],
  );
  const withoutDeviceFields = useCallback(
    (d: Demo) => {
      if (!deviceDemoKeys.length) return d;
      const out = { ...d };
      for (const k of deviceDemoKeys) delete out[k];
      return out;
    },
    [deviceDemoKeys],
  );

  // Prepopulate from the actual scanner as soon as it is known. Only blanks — a value the operator
  // corrected must survive a reconnect.
  /*
   * Seed the workflow's default values.
   *
   * Blanks only — see lib/workflows/defaults. Order against the draft load does not matter for the same
   * reason: a default never overwrites, so whichever runs second cannot undo the other.
   */
  /*
   * What this customer last submitted, which outranks both the workflow's default and the shipped one.
   *
   * Fetched once and cached: it changes only when they submit, and a stale read costs nothing because
   * defaults fill blanks only. `staleTime` keeps a tab switch from re-asking.
   */
  const prefs = useQuery({
    queryKey: ['field-prefs'],
    queryFn: () => api.get<{ values: Record<string, string> }>('/field-prefs'),
    staleTime: 5 * 60_000,
  });

  useEffect(() => {
    const fs = wf?.fields;
    if (!fs?.length) return;
    /*
     * Waits for the remembered values before seeding.
     *
     * Running first with the shipped defaults would fill the boxes, and then their own codes — arriving a
     * moment later — would find nothing blank to fill. The operator would watch the form settle on 346W
     * and have to retype theirs, which is the complaint this exists to answer.
     */
    if (prefs.isLoading) return;
    setDemo((prev) => withDefaults(fs, prev, prefs.data?.values ?? {}));
  }, [wf, prefs.isLoading, prefs.data]);

  useEffect(() => {
    /*
     * Taken from the RULES, not from `postRules`.
     *
     * `strip()` on the server folds the legacy flags into `rules` and deletes `postRules` on the way
     * past, so reading it here meant reading `undefined` on every workflow the API serves — the fields
     * sat blank, and since they are `required` the operator typed them. See lib/workflows/deviceFields.
     */
    const map = deviceFields;
    const dev = conn.device;
    if (!dev) return;
    const pairs: [string | undefined, string | null | undefined][] = [
      [map.make, dev.vendorName],
      [map.model, dev.deviceName],
      [map.serial, dev.deviceSerial],
    ];
    setDemo((prev) => {
      let next = prev;
      for (const [name, value] of pairs) {
        if (name && value && !prev[name]) {
          if (next === prev) next = { ...prev };
          next[name] = value;
        }
      }
      return next;
    });
    // `deviceFields` rather than `wf`: it is what this effect reads, and it is memoised from the workflow.
  }, [deviceFields, conn.device]);

  const snapshot = snapshotOf(withoutDeviceFields(demo), prints, ampCodes, docKeys);
  const hasWork = snapshot !== EMPTY_SNAPSHOT;
  // Not dirty while a draft is still loading (nothing to lose yet), nor after submission succeeded.
  const dirty = hasWork && !result && !loadingDraft && snapshot !== savedSnapshot;
  const guard = useUnsavedGuard(dirty);
  /*
   * `dirty` is the same signal the unsaved-work guard uses, so "has work in progress" means exactly what it
   * means everywhere else in this flow — a reload here would discard staged impressions and typed demographics,
   * which is why a stale bundle is only stopped before any of that exists.
   */
  const { stale: bundleStale } = useAppVersion();

  /**
   * The DTO this submission is built from, kept so the SAME one is serialized every time.
   *
   * The TCN is rendered inside buildCustomEft from {datetime} and {rand:N}, so a second build produces a
   * different one. Rebuilding — which the retry button does when the first Device Service call failed —
   * therefore produced a file whose TCN no longer matched the recorded transaction. Held in a ref, not
   * state, because the submit path reads it synchronously in the same tick it is written.
   *
   * Declared HERE, with the other hooks, and not beside the functions that use it. Below this point the
   * component returns early — while the workflow list loads, while a draft opens, when the workflow is not
   * authorized — so a hook further down runs on some renders and not others. React counts hooks per render
   * and refuses a count that changes: it threw #310 on any refresh of the capture page, because the first
   * render returned at the loading guard and the second went past it.
   */
  const dtoRef = useRef<ReturnType<typeof buildCustomEft> | null>(null);

  if (!type) {
    return <div className="panel empty"><h3>Unknown workflow</h3><Link to="/app/capture" className="track-link">Back to workflows</Link></div>;
  }

  if (wf?.requiresAuthorization) {
    if (account.isLoading) return <div className="loading-screen">// checking access…</div>;
    if (gatedBlocked) {
      return (
        <div className="panel empty">
          <h3>This workflow isn't enabled for your account</h3>
          <p>
            {/* The workflow's own label, so a newly configured one names itself instead of guessing. */}
            {wf.label} transactions are provisioned per account.{' '}
            <button type="button" className="linklike" onClick={openSupport}>Ask the assistant</button> to enable them.
          </p>
          <Link to="/app/capture" className="track-link">Back to workflows</Link>
        </div>
      );
    }
  }

  if (workflows.isLoading) return <div className="loading-screen">// loading workflow…</div>;
  if (!wf) {
    return <div className="panel empty"><h3>Unknown workflow</h3><Link to="/app/capture" className="track-link">Back to workflows</Link></div>;
  }
  if (loadingDraft) return <div className="loading-screen">// opening your saved draft…</div>;

  // Blocked at the door only: an untouched flow can reload freely, a started one must not be interrupted.
  if (shouldBlockStart(bundleStale, dirty)) return <StaleGate />;

  const fields = wf.fields;
  const captureStepLabel = isConversion ? 'Documents' : 'Fingerprints';
  const missing = fields.filter((f) => isFieldInvalid(f, demo[f.name]));
  /*
   * Fingerprint completeness, checked again at the door of the build.
   *
   * The capture step already refuses to advance, but a draft resumed on a workflow whose positions changed
   * since it was saved reaches review without ever passing through that gate. A conversion has no
   * impressions at all, so it is exempt.
   */
  const printsOutstanding = isConv ? [] : missingPositions(wf?.biometrics?.fingerprintPositions, prints, ampCodes);
  // A present-but-malformed value is not "missing", and saying so sends the operator looking for an empty
  // box that isn't there. Height is the common case: 5'9" typed as 59, or inches over 11.
  const blank = missing.filter((f) => !(demo[f.name] ?? '').trim());
  const malformed = missing.filter((f) => (demo[f.name] ?? '').trim());

  // Biometric consent is required per transaction only when the account retains fingerprints (secure
  // storage active). DOCE submits to the FBI channel (not secure storage) → no consent gate.
  const consentRequired = Boolean(account.data?.storageActive) && type !== 'doce';
  // One statement for every customer — no per-account override.
  const consentStatement = DEFAULT_CONSENT_STATEMENT;
  const consentComplete =
    !consentRequired || (consentAccepted && (isConversion || consentName.trim().length > 0));

  /** Save (or update) the draft for this workflow and mark the form clean. */
  async function saveDraftNow(): Promise<boolean> {
    setDraftBusy(true);
    setDraftErr(null);
    setDraftMsg(null);
    try {
      const payload: DraftPayload = {
        v: 1, captureType: type!, demo, prints, ampCodes, docKeys, step,
      };
      const id = await saveDraft(payload, draftId ?? undefined);
      setDraftId(id);
      // Snapshot the state as saved, not the state at the moment the response lands — anything the
      // operator changed while the upload was in flight must still count as dirty.
      setSavedSnapshot(snapshot);
      setDraftMsg('Draft saved — it stays available for 30 days.');
      return true;
    } catch (e) {
      setDraftErr(e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'Could not save the draft');
      return false;
    } finally {
      setDraftBusy(false);
    }
  }

  /**
   * Build fresh for a submission attempt, and remember it.
   *
   * Always rebuilds: an attempt that failed is often followed by the operator correcting something and
   * submitting again, and reusing the earlier DTO would silently discard that correction — a worse fault
   * than the TCN mismatch this caching exists to prevent.
   */
  function newDto(): ReturnType<typeof buildCustomEft> {
    const dto = buildCustomEft(wf!, demo, prints, ampCodes, device, facePhoto);
    dtoRef.current = dto;
    return dto;
  }

  /**
   * The DTO this transaction was RECORDED from, for anything that serializes after the fact.
   *
   * The retry-download button rebuilt from scratch, which re-rendered the TCN — so a customer whose first
   * Device Service call failed got a file whose TCN did not match their own transaction record.
   */
  function submittedDto(): ReturnType<typeof buildCustomEft> {
    return dtoRef.current ?? newDto();
  }

  /** Hand a built DTO to the Device Service for serialization. */
  async function serializeEft(dto: ReturnType<typeof buildCustomEft>): Promise<string> {
    const ds = conn.svc ?? (await DeviceService.connect());
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken?.toString() ?? '';
    const res = await ds.buildCustomEft(dto, {
      region: config.region,
      endpoint: config.apiEndpoint,
      issuer: `https://cognito-idp.${config.region}.amazonaws.com/${config.userPoolId}`,
      idToken,
    });
    return res.ebtsFile;
  }

  async function buildEft(): Promise<string> {
    return serializeEft(submittedDto());
  }

  async function submit(opts?: { purchase?: boolean }) {
    setSubmitting(true);
    setError(null);
    setPayRequired(false);
    try {
      let photoStored = false;
      if (isConversion) {
        // Build the Type-1/Type-2 ANI skeleton (no live prints) — the auto-segmenter fills in the
        // Type-4 records from the scanned card image.
        const skeletonDto = buildCustomEft(wf!, demo, {}, {}, null);
        const skeleton = skeletonDto.transaction;
        // The segmenter fills in the images and keeps this Type-1, so the skeleton's TCN is the finished
        // file's TCN — sent for the same reason as the live-capture path above.
        const skeletonTcn = tcnOfDto(skeletonDto);
        const res = await api.post<{ transactionId: string; tcn: string; billing?: string }>('/conversion', {
          captureType: type, tot: wf!.tot,
          ...(skeletonTcn ? { tcn: skeletonTcn } : {}),
          firstName: demo['firstName'], lastName: demo['lastName'], dob: demo['dateOfBirth'], email: demo['email'],
          imageKeys: docKeys,
          transaction: skeleton,
          ...(opts?.purchase ? { purchase: true } : {}),
          ...(consentRequired ? { consent: { method: 'operator', accepted: true } } : {}),
        });
        setResult({
          tcn: res.tcn,
          msg: `Card conversion submitted${res.billing === 'purchase' ? ' — your card was charged for this conversion' : ''}. You can track it on the Transactions page.`,
        });
      } else if (type === 'doce') {
        const ebts = await serializeEft(newDto());
        const res = await api.post<{ transactionId: string; tcn: string }>('/doce/submit', {
          ebts, device: deviceIdentity(device, dsVersion),
          firstName: demo['firstName'], lastName: demo['lastName'], dob: demo['dateOfBirth'],
          // The channeler reads `applicant.email` / `applicant.phone` to create the electronic-delivery
          // account. The address is NOT part of this file — it travels in the EBTS as Type-2 field 41.
          // The server rebuilds this into the required shape regardless, so a cached bundle still works.
          applicantJson: { applicant: { email: demo['email'], phone: demo['phone'] } },
        });
        setResult({ tcn: res.tcn, msg: 'DOCE submitted to the FBI channel.' });
      } else {
        /*
         * Built BEFORE the transaction is recorded, so the TCN sent is the one the file will carry.
         *
         * The post used to happen first with no TCN at all, and the server then invented
         * `EFTC-<transactionId>` — so every ATF and SWFT transaction was listed under a TCN that appears
         * nowhere in the customer's file, and support could not find a submission by the number on it. The
         * DTO is cached, so serializing it below cannot produce a different one.
         */
        const dto = newDto();
        const builtTcn = tcnOfDto(dto);
        const res = await api.post<{ transactionId: string; tcn: string; billingMode: string }>('/capture/eft', {
          captureType: type, tot: wf!.tot,
          firstName: demo['firstName'], lastName: demo['lastName'], dob: demo['dateOfBirth'],
          device: deviceIdentity(device, dsVersion),
          ...(builtTcn ? { tcn: builtTcn } : {}),
          ...(rebuildOfParam ? { rebuildOfTcn: rebuildOfParam } : {}),
          ...(consentRequired ? { consent: { method: 'subject', subjectName: consentName.trim(), accepted: true } } : {}),
        });
        const filename = `${artifactStem(demo, type)}.eft`;
        let ebts: string | undefined;
        let buildErr: string | undefined;
        try { ebts = await serializeEft(dto); } catch (e) { buildErr = e instanceof Error ? e.message : 'The Device Service could not build the .eft'; }
        let stored = false;
        let shownTcn = res.tcn;
        if (ebts) {
          downloadEft(ebts, filename);
          /*
           * The photograph is a separate artifact from the EFT, so it downloads as its own file. ATF
           * wants exactly this: the .eft goes to one place and the photograph is uploaded to their
           * portal by hand.
           */
          if (wf?.face?.targets.includes('download') && facePhoto) {
            downloadPhoto(facePhoto, filename.replace(/\.eft$/i, '.png'));
          }
          /*
           * The photograph rides along with the EFT rather than in its own request: one round trip, and
           * the endpoint already checks that storage is active and that the tier has room.
           */
          const wantsStored = Boolean(wf?.face?.targets.includes('storage') && facePhoto);
          stored = await api
            .post<{ stored?: boolean; photoStored?: boolean; tcn?: string }>(
              `/transactions/${encodeURIComponent(res.tcn)}/store`,
              { ebts, ...(wantsStored ? { photo: facePhoto } : {}) },
            )
            .then((r) => {
              // The server reads the TCN out of the file it just stored; if it disagreed with the record it
              // says so, and the customer should see what their own file carries.
              if (r?.tcn) shownTcn = r.tcn;
              // Recorded so the result can say whether the photograph was kept. It used to be swallowed
              // with the EFT's own outcome, so a photograph that never reached storage said nothing.
              photoStored = Boolean(r?.photoStored);
              return true;
            })
            .catch(() => false);
        }
        setResult({
          tcn: shownTcn,
          ...(ebts ? { ebts, filename } : {}),
          stored,
          msg: ebts
            ? `EFT recorded (${res.billingMode}). Your .eft has downloaded${stored ? ' and been saved to your secure storage' : ''}.`
              /*
               * The photograph gets its own sentence, and gets one when it was NOT kept as well. It was
               * previously folded into the EFT's outcome, so an operator who took a photograph the account
               * had nowhere to store was told nothing at all about it.
               */
              + (facePhoto
                  ? photoStored
                    ? ' The photograph was saved with the transaction.'
                    : wf?.face?.targets.includes('storage')
                      ? ' The photograph was NOT saved — this account does not have Secure Storage.'
                      : ''
                  : '')
              + " If the download didn't start, use the button below."
            : `EFT recorded (${res.billingMode}), but the .eft could not be built from the Device Service${buildErr ? ` (${buildErr})` : ''}. Use the button below to build and download it.`,
        });
      }
      /*
       * Remember the office codes this operator actually used, so the next transaction starts with
       * theirs rather than the shipped ones.
       *
       * After the submission, never as they type: the values worth keeping are the ones that went out,
       * and storing keystrokes would carry a half-typed code into the next capture. Best-effort for the
       * same reason the draft cleanup below is — failing to remember a convenience must not surface as a
       * failed submission on work that succeeded.
       */
      void api.put('/field-prefs', { values: rememberable(demo) }).catch(() => undefined);

      // The work is now a real transaction — drop the draft so it doesn't linger in the list.
      // Best-effort: a failed cleanup would otherwise mask a successful submission, and the 30-day
      // sweep collects it anyway.
      if (draftId) {
        await deleteDraft(draftId).catch(() => undefined);
        setDraftId(null);
      }
      /*
       * The staged capture has done its job. Dropped now rather than left to its two-day expiry: the
       * submission carries the imagery, and what is in the cache is a set of fingerprints belonging to
       * someone who has finished and left.
       */
      aniCache.discard();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'Submission failed');
      if (e instanceof ApiError && e.status === 402) setPayRequired(true);
    } finally {
      setSubmitting(false);
    }
  }

  async function downloadResult() {
    if (!result) return;
    setDlBusy(true);
    setDlErr(null);
    try {
      let ebts = result.ebts;
      let stored = result.stored ?? false;
      if (!ebts) {
        ebts = await buildEft();
        stored = await api.post(`/transactions/${encodeURIComponent(result.tcn ?? '')}/store`, { ebts }).then(() => true).catch(() => false);
      }
      const filename = result.filename ?? `${artifactStem(demo, type)}.eft`;
      downloadEft(ebts, filename);
      setResult({ ...result, ebts, filename, stored });
    } catch (e) {
      setDlErr(e instanceof Error ? e.message : 'Could not build the EFT from the Device Service');
    } finally {
      setDlBusy(false);
    }
  }

  if (result) {
    const canDownload = !isConversion && type !== 'doce';
    return (
      <div className="panel capture-done">
        <div className="done-check">✓</div>
        <h2>Submission complete</h2>
        <p>{result.msg}</p>
        {result.tcn && <p className="mono done-tcn">{result.tcn}</p>}
        {dlErr && <p className="cart-error">{dlErr}</p>}
        <div className="done-actions">
          {canDownload && (
            <button className="btn btn-primary" onClick={downloadResult} disabled={dlBusy}>
              {dlBusy ? 'Building…' : result.ebts ? '⤓ Download .eft again' : '⤓ Build & download .eft'}
            </button>
          )}
          <Link to="/app/capture" className="btn btn-ghost">New transaction</Link>
          <Link to="/app" className="btn btn-ghost">Back to dashboard</Link>
        </div>
      </div>
    );
  }

  /*
   * The photograph sits between the prints and the review: it is part of collecting the subject, but
   * the prints are what the operator is there for and should not wait behind a camera.
   */
  const faceStep: { key: Step; label: string }[] = wf?.face ? [{ key: 'face', label: 'Photograph' }] : [];
  const steps: { key: Step; label: string }[] = isConversion
    ? [{ key: 'demographics', label: 'Demographics' }, { key: 'capture', label: captureStepLabel }, ...faceStep, { key: 'review', label: 'Review & submit' }]
    : [{ key: 'device', label: 'Scanner' }, { key: 'demographics', label: 'Demographics' }, { key: 'capture', label: 'Fingerprints' }, ...faceStep, { key: 'review', label: 'Review & submit' }];
  const stepIndex = steps.findIndex((s) => s.key === step);
  const demoIndex = steps.findIndex((s) => s.key === 'demographics');
  // Report the Demographics tab's real state once it's been left or skipped past. Without this the tab
  // showed a green check purely because the operator had moved on, contradicting the missing-data error
  // raised at submit.
  const demoEvaluated = demoVisited || stepIndex > demoIndex;

  /**
   * Why a step cannot be opened yet, or undefined when it can.
   *
   * Only the scanner step gates the others, and only for live capture — a card conversion never touches the
   * Device Service, so its steps are always reachable.
   */
  function stepBlockedReason(next: Step): string | undefined {
    if (isConversion || next === 'device' || deviceReady) return undefined;
    if (conn.status !== 'connected') return 'Connect your scanner first — the Device Service is required for this workflow.';
    if (!conn.device?.deviceSerial) return 'No scanner detected. Plug one in and rescan before continuing.';
    return 'Finish the scanner check first — this workstation is not ready to capture.';
  }

  /** Step navigation. Leaving Demographics locks in its validity and reveals field-level errors. */
  function goToStep(next: Step) {
    // Guarded here as well as on the pill: every other caller of goToStep goes through this one door.
    if (stepBlockedReason(next)) return;
    if (step === 'demographics' && next !== 'demographics') {
      setDemoVisited(true);
      if (missing.length) setShowDemoErrors(true);
    }
    setStep(next);
  }

  return (
    <div>
      <div className="console-head">
        <div>
          <h1>{wf.label}</h1>
          <div className="sub"><Link to="/app/capture" className="track-link">← Workflows</Link> · <span className="mono">{wf.specification} · {wf.tot}</span></div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div className="draft-bar">
            <button className="btn btn-ghost btn-sm" onClick={() => void saveDraftNow()}
              disabled={draftBusy || !hasWork || Boolean(result)}
              title={hasWork ? 'Save this transaction and finish it later' : 'Nothing to save yet'}>
              {draftBusy ? 'Saving…' : draftId ? 'Save draft' : 'Save as draft'}
            </button>
            <span className={`draft-state${dirty ? ' is-dirty' : ''}`}>
              {dirty ? 'Unsaved changes' : draftId ? 'Draft saved' : ''}
            </span>
          </div>
          {!isConversion && <DeviceStatusChip conn={conn} />}
        </div>
      </div>

      {draftMsg && !dirty && <div className="banner-ok" style={{ marginBottom: 14 }}>{draftMsg}</div>}
      {draftErr && <p className="cart-error" style={{ marginTop: 0 }}>{draftErr}</p>}

      <div className="stepper">
        {steps.map((s, i) => {
          const isDemo = s.key === 'demographics';
          const invalid = isDemo && demoEvaluated && missing.length > 0;
          const done = isDemo ? demoEvaluated && missing.length === 0 : i < stepIndex;
          return (
            <StepDot key={s.key} n={i + 1} label={s.label} active={step === s.key} done={done} invalid={invalid}
              blocked={stepBlockedReason(s.key)}
              onClick={() => goToStep(s.key)} />
          );
        })}
      </div>

      {/*
        * Said on the page, not just implied by the absence of a charge: an operator recovering a broken file
        * should not have to wonder whether they are about to pay for it twice.
        */}
      {rebuildOfParam && (
        <div className="banner-ok" style={{ marginBottom: 12 }}>
          Rebuilding <span className="mono">{rebuildOfParam}</span> — this replaces that transaction and is not
          charged again.
        </div>
      )}
      {step === 'device' && (
        <DeviceStep conn={conn} readiness={readiness} onReady={() => goToStep('demographics')} />
      )}

      {step === 'demographics' && (
        <div className="panel">
          <div className="scan-hint">
            <span className="scan-hint-ico" aria-hidden>🪪</span>
            <span>
              Tip: scan the barcode on the back of a driver&rsquo;s license to auto-fill these fields.{' '}
              {/* The setup page, linked from the one screen where a misconfigured scanner shows up. */}
              <Link to="/app/help/barcode-scanner" target="_blank">Scanner setup &rarr;</Link>
            </span>
          </div>
          {scanMsg && <div className="banner-ok" style={{ marginBottom: 14 }}>{scanMsg}</div>}
          {wf && !isConv && (
            <ImportEft
              workflow={wf}
              onImported={(result, imported) => {
                setDemo((d) => ({ ...d, ...imported }));
                /*
                 * The imagery stays where the server put it. Adopting the cache means validation and the
                 * assembled EFT use the ORIGINAL WSQ, untouched — the capture grid is not populated from
                 * it, because the grid holds displayable PNG and filling it from a thumbnail would put a
                 * 220px image where a fingerprint belongs.
                 */
                const byFgpForAdopt = new Map(Object.entries(FGP).map(([scanType, fgp]) => [fgp, scanType]));
                aniCache.adopt(
                  result.cacheId,
                  result.images.flatMap((i) => {
                    const key = byFgpForAdopt.get(i.fgp);
                    // Registered under the GRID's key so the watcher recognises it as already staged;
                    // the cache's own name for it is kept for fetching and removing it.
                    return key ? [{ key, serverSlot: i.slot, data: i.wsq }] : [];
                  }),
                );
                /*
                 * Filed under the scan position the grid uses, so an imported print occupies the same
                 * cell a captured one would and can be re-taken over the top. `prints` holds the WSQ —
                 * it is what the EFT carries and re-encoding it through PNG would compress a print that
                 * was already correctly compressed — and the thumbnail is what gets drawn.
                 */
                const byFgp = new Map(Object.entries(FGP).map(([scanType, fgp]) => [fgp, scanType]));
                const nextPrints: Record<string, string> = {};
                const nextThumbs: Record<string, { src: string; forData: string; slot: string }> = {};
                for (const img of result.images) {
                  const scanType = byFgp.get(img.fgp);
                  if (!scanType) continue; // a position this workflow's grid does not show
                  nextPrints[scanType] = img.wsq;
                  nextThumbs[scanType] = { src: img.thumbnail, forData: img.wsq, slot: img.slot };
                }
                setPrints((prev) => ({ ...prev, ...nextPrints }));
                setPrintThumbs((prev) => ({ ...prev, ...nextThumbs }));
              }}
            />
          )}
          <div className="demo-grid">
            {fields.map((f) => (
              <FieldInput key={f.name} f={f} value={demo[f.name] ?? ''}
                options={f.lookup ? lookupOptions(wf.specification, f.lookup) : f.options}
                onChange={(v) => setDemo((d) => ({ ...d, [f.name]: v }))}
                invalid={showDemoErrors && isFieldInvalid(f, demo[f.name])} />
            ))}
          </div>
          <div className="flow-actions">
            {!isConversion && <button className="btn btn-ghost" onClick={() => goToStep('device')}>← Scanner</button>}
            {missing.length > 0 && (
              <span className="flow-hint">{missing.length} required field(s) left — you can capture {isConversion ? 'documents' : 'fingerprints'} first and complete these before submitting.</span>
            )}
            {/* Continue is always allowed so the operator can do fingerprints first; required fields are
                enforced (and highlighted) at Review before submission. */}
            <button className="btn btn-primary" onClick={() => goToStep('capture')}>Continue →</button>
          </div>
        </div>
      )}

      {step === 'capture' && !isConversion && (
        <Fingerprints
          conn={conn}
          quality={quality} setQuality={setQuality}
          prints={prints} setPrints={setPrints} printThumbs={printThumbs} cacheId={aniCache.cacheId}
          ampCodes={ampCodes} setAmpCodes={setAmpCodes}
          positions={wf?.biometrics?.fingerprintPositions}
          onBack={() => goToStep('demographics')} onNext={() => goToStep(wf?.face ? 'face' : 'review')}
        />
      )}

      {step === 'capture' && isConversion && (
        <Documents docKeys={docKeys} setDocKeys={setDocKeys} onBack={() => goToStep('demographics')} onNext={() => goToStep(wf?.face ? 'face' : 'review')} />
      )}

      {step === 'face' && wf?.face && (
        <FaceCapture
          config={wf.face}
          storageActive={Boolean(account.data?.storageActive)}
          photo={facePhoto}
          onChange={setFacePhoto}
          /*
           * Named from the demographics already entered, so a photograph saved here matches the .eft that
           * downloads later rather than arriving as `download.png` among a dozen others.
           */
          onDownload={() => facePhoto && downloadPhoto(facePhoto, `${artifactStem(demo, type)}.png`)}
          onBack={() => goToStep('capture')}
          onNext={() => goToStep('review')}
        />
      )}

      {step === 'review' && (
        <div className="panel">
          <h3 className="review-h">Review</h3>
          <div className="review-grid">
            {fields.filter((f) => demo[f.name]).map((f) => (
              <div key={f.name} className="review-row"><span>{f.label}</span><strong>{demo[f.name]}</strong></div>
            ))}
            <div className="review-row"><span>{captureStepLabel}</span>
              <strong>
                {isConversion
                  ? `${docKeys.length} image(s)`
                  : `${Object.keys(prints).length} captured${Object.keys(ampCodes).length ? `, ${Object.keys(ampCodes).length} amputated/unprintable` : ''}`}
              </strong>
            </div>
          </div>
          {!isConversion && (
            <EbtsValidation
              cacheId={aniCache.cacheId}
              dto={reviewDto}
              pending={aniCache.pending}
              onResult={(r) => { setEbts(r); setEbtsAcknowledged(false); }}
            />
          )}
          {consentRequired && (
            <div className="consent-block" style={{ marginTop: 18 }}>
              <div className="field-label" style={{ marginBottom: 6 }}>Biometric-consent acknowledgment</div>
              <p className="cart-note" style={{ textAlign: 'left', marginTop: 0 }}>
                Your account securely stores fingerprint data, so consent is required for this submission.
              </p>
              <div className="panel" style={{ background: 'var(--surface-2)', whiteSpace: 'pre-wrap', fontSize: 13, lineHeight: 1.5 }}>
                {consentStatement}
              </div>
              {!isConversion ? (
                <>
                  <label className="field" style={{ maxWidth: 420, marginTop: 12 }}>
                    <span className="field-label">Subject's full name (typed signature)</span>
                    <input className="input" value={consentName} onChange={(e) => setConsentName(e.target.value)}
                      placeholder="Full legal name" />
                  </label>
                  <label className="checkbox-row" style={{ marginTop: 12 }}>
                    <input type="checkbox" checked={consentAccepted} onChange={(e) => setConsentAccepted(e.target.checked)} />
                    <span>The subject named above has read and consents to the statement above.</span>
                  </label>
                </>
              ) : (
                <label className="checkbox-row" style={{ marginTop: 12 }}>
                  <input type="checkbox" checked={consentAccepted} onChange={(e) => setConsentAccepted(e.target.checked)} />
                  <span>I attest that I have obtained the subject's written consent to the statement above.</span>
                </label>
              )}
            </div>
          )}
          {error && <p className="cart-error">{error}</p>}
          {payRequired && (
            isConversion ? (
              <div className="purchase-submit">
                <button className="btn btn-primary" onClick={() => submit({ purchase: true })} disabled={submitting || !consentComplete || missing.length > 0 || printsOutstanding.length > 0}>
                  {submitting ? 'Processing…' : `Purchase & submit${convCredit.data?.unitAmount != null ? ` (${money(convCredit.data.unitAmount, convCredit.data.currency)})` : ''}`}
                </button>
                <span className="flow-hint">
                  Charges your saved card for one conversion — or{' '}
                  <Link to="/app/subscriptions" className="track-link">buy credits / subscribe to a conversion plan</Link>.
                </span>
              </div>
            ) : (
              <p className="cart-note" style={{ textAlign: 'left' }}>
                → <Link to="/app/subscriptions" className="track-link">Choose a plan or allocate a device license</Link> to continue.
              </p>
            )
          )}
          {missing.length > 0 && (
            <div className="banner-warn" style={{ marginTop: 14 }}>
              {blank.length > 0 && (
                <>
                  <strong>Missing required {isConversion ? 'information' : 'demographic data'}:</strong>{' '}
                  {blank.map((f) => f.label).join(', ')}.{' '}
                </>
              )}
              {malformed.length > 0 && (
                <>
                  <strong>Invalid format:</strong> {malformed.map((f) => f.label).join(', ')}.{' '}
                </>
              )}
              <button className="linklike" onClick={() => { setShowDemoErrors(true); goToStep('demographics'); }}>Complete demographics →</button>
            </div>
          )}
          {/*
            * Fingerprints, said as plainly as the demographics above it. Reachable when a draft was saved
            * before the workflow's positions changed, so the operator is told which impressions the
            * workflow now wants rather than left with a disabled button and no reason.
            */}
          {printsOutstanding.length > 0 && (
            <div className="banner-warn" style={{ marginTop: 14 }}>
              <strong>Missing impressions:</strong> {describePositions(printsOutstanding)}.{' '}
              Capture each one, or mark it amputated or unprintable.{' '}
              <button className="linklike" onClick={() => goToStep('capture')}>Go to fingerprints →</button>
            </div>
          )}
          {/*
            * A failing validation warns and asks; it does not block. The specification is a rulebook,
            * not the FBI's own acceptance, and refusing a submission it flags would strand transactions
            * the FBI would have taken — with the subject already fingerprinted and gone.
            */}
          {ebtsBlocking && (
            <label className="checkbox-row ebts-ack">
              <input type="checkbox" checked={ebtsAcknowledged} onChange={(e) => setEbtsAcknowledged(e.target.checked)} />
              <span>
                This EFT has {ebts!.issues.length} validation issue{ebts!.issues.length === 1 ? '' : 's'} against{' '}
                {ebts!.specification.replace(/_/g, ' ')}. Submit it anyway — I understand it may be rejected.
              </span>
            </label>
          )}
          <div className="flow-actions">
            <button className="btn btn-ghost" onClick={() => goToStep('capture')}>← Back</button>
            {consentRequired && !consentComplete && (
              <span className="flow-hint is-blocking">Complete the consent acknowledgment above to submit.</span>
            )}
            {/* The disabled button alone did not explain itself: an operator pressing it concluded the
                site was ignoring them rather than that something above was outstanding. */}
            {ebtsBlocking && !ebtsAcknowledged && (
              <span className="flow-hint is-blocking">Acknowledge the validation issues above to submit.</span>
            )}
            <button
              className="btn btn-primary"
              onClick={() => submit()}
              disabled={submitting || !consentComplete || missing.length > 0 || printsOutstanding.length > 0 || (ebtsBlocking && !ebtsAcknowledged)}
            >
              {submitting ? 'Submitting…' : isConversion ? 'Submit conversion' : type === 'doce' ? 'Submit to FBI' : 'Generate & submit EFT'}
            </button>
          </div>
        </div>
      )}

      {guard.pending && (
        <LeaveConfirm
          busy={draftBusy}
          onSave={async () => { if (await saveDraftNow()) guard.proceed(); }}
          onDiscard={guard.proceed}
          onStay={guard.cancel}
        />
      )}
    </div>
  );
}

/**
 * Shown when the operator tries to navigate away mid-capture. Offers the save they actually want,
 * rather than only the destructive yes/no a plain confirm would give.
 */
function LeaveConfirm({ busy, onSave, onDiscard, onStay }: {
  busy: boolean; onSave: () => void; onDiscard: () => void; onStay: () => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !busy) onStay(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [busy, onStay]);

  return (
    <div className="confirm-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onStay(); }}>
      <div className="confirm-card" role="dialog" aria-modal="true" aria-labelledby="leave-title">
        <h3 className="confirm-title" id="leave-title">Leave without saving?</h3>
        <div className="confirm-body">
          <p>
            This transaction has demographic data and/or fingerprints that haven't been saved. Save it
            as a draft and you can pick it up from the Transactions page.
          </p>
          <p className="confirm-policy">Drafts are kept for 30 days, then removed automatically.</p>
        </div>
        <div className="confirm-actions" style={{ flexWrap: 'wrap' }}>
          <button className="btn btn-ghost" onClick={onStay} disabled={busy}>Stay here</button>
          <button className="btn btn-ghost" onClick={onDiscard} disabled={busy}>Discard &amp; leave</button>
          <button className="btn btn-primary" onClick={onSave} disabled={busy}>
            {busy ? 'Saving…' : 'Save draft & leave'}
          </button>
        </div>
      </div>
    </div>
  );
}

function money(amount: number, currency: string) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency.toUpperCase() }).format(amount / 100);
}

function deviceIdentity(d: DeviceInfo | null, deviceServiceVersion?: string | null) {
  if (!d) return undefined;
  return {
    serialNumber: d.deviceSerial,
    installationId: String(d.deviceId),
    ...(d.deviceVersion ? { firmware: d.deviceVersion } : {}),
    // The server enforces a minimum Device Service version; it can only do that if it is told one.
    ...(deviceServiceVersion ? { deviceServiceVersion } : {}),
  };
}

/**
 * The photograph, as its own download.
 *
 * Separate from downloadEft rather than generalised: the EFT is an octet-stream the browser must not
 * try to interpret, while this is an image a browser will happily preview, and conflating the two only
 * makes both harder to read.
 */
/**
 * What a downloaded artifact for this subject is called: `LAST_FIRST_<type>`.
 *
 * Shared by the EFT and the photograph so the two files sit together in a downloads folder and are
 * obviously the same transaction — which is the whole point when ATF wants the .eft in one place and the
 * photograph uploaded to their portal by hand.
 */
function artifactStem(demo: Record<string, string>, type: string | undefined): string {
  /*
   * `type` is optional at these call sites, and the template literal it replaces interpolated it
   * unchecked — so a transaction without one produced `SMITH_undefined_undefined.eft`. Falling back to
   * `eft` keeps the name meaningless rather than actively wrong.
   */
  const kind = (type ?? '').trim() || 'eft';
  return `${demo['lastName'] || 'eft'}_${demo['firstName'] || kind}_${kind}`.replace(/\s+/g, '');
}

function downloadPhoto(base64: string, filename: string) {
  const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
  const url = URL.createObjectURL(new Blob([bytes], { type: 'image/png' }));
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  setTimeout(() => { a.remove(); URL.revokeObjectURL(url); }, 1500);
}

function downloadEft(base64: string, filename: string) {
  const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
  const url = URL.createObjectURL(new Blob([bytes], { type: 'application/octet-stream' }));
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  setTimeout(() => { a.remove(); URL.revokeObjectURL(url); }, 1500);
}

function StepDot({ n, label, active, done, invalid, onClick, blocked }: {
  n: number; label: string; active: boolean; done: boolean; invalid?: boolean | undefined;
  onClick?: () => void;
  /** Why this step cannot be opened yet, or undefined when it can. */
  blocked?: string | undefined;
}) {
  return (
    <button type="button"
      className={`step-dot${active ? ' active' : ''}${done ? ' done' : ''}${invalid ? ' invalid' : ''}${blocked ? ' blocked' : ''}`}
      onClick={onClick} disabled={!onClick || Boolean(blocked)}
      title={blocked ?? (invalid ? `${label} — required fields are missing or invalid` : undefined)}>
      <span className="step-dot-n">{invalid ? '!' : done ? '✓' : n}</span>
      <span className="step-dot-label">{label}</span>
    </button>
  );
}

// ---------------- Device Service status chip (shared, sits by the page title) ----------------
function DeviceStatusChip({ conn }: { conn: DeviceConn }) {
  const { status, device } = conn;
  const connected = status === 'connected';
  const state = connected
    ? device ? device.deviceName : 'No scanner detected'
    : status === 'connecting' ? 'Connecting…'
    : status === 'insecure' ? 'Unreachable — use Chrome/Edge'
    : status === 'blocked' ? 'Blocked by browser'
    : status === 'unavailable' ? 'Not detected'
    : 'Connecting…';
  const full = connected
    ? device ? `Connected — ${device.deviceName} (${device.deviceSerial})` : 'Connected — no scanner detected'
    : state;
  const action = connected ? 'Rescan' : 'Retry';
  return (
    <div className={`dev-chip dev-${status}`} title={full}>
      <span className="dev-chip-dot" />
      <span className="dev-chip-text">
        <span className="dev-chip-title">Device Service</span>
        <span className="dev-chip-state">{state}</span>
      </span>
      <button className="dev-chip-refresh" onClick={() => void conn.connect()} disabled={status === 'connecting'}
        title={action} aria-label={action}>
        <FontAwesomeIcon icon={faArrowsRotate} spin={status === 'connecting'} />
      </button>
    </div>
  );
}

// ---------------- Step 1 (live capture): scanner connectivity + entitlement precheck ----------------

/**
 * The scanner tab. It DISPLAYS the readiness answer; `useCaptureReadiness` in the flow computes it.
 *
 * The check used to live here, which meant it only ran while this tab was on screen — see the note on that
 * hook for what that cost.
 */
function DeviceStep({ conn, readiness, onReady }: {
  conn: DeviceConn;
  readiness: Readiness;
  onReady: () => void;
}) {
  const { minVersion } = useDeviceServiceStatus();
  const { check, checking, ready } = readiness;
  const recheck = readiness.recheck;
  const connected = conn.status === 'connected';
  const serial = conn.device?.deviceSerial;

  return (
    <div className="panel">
      <h3 className="review-h">Connect your scanner</h3>
      <p className="cart-note" style={{ textAlign: 'left', marginTop: 0 }}>
        // We check the Device Service and your plan before you enter any applicant data, so you don't fill out the form only to hit a snag. Its status &amp; the rescan control are up by the title.
      </p>

      {conn.status === 'unavailable' && (
        <p className="device-help">The EFTCreator Device Service must be running on this workstation to capture fingerprints. Start it, then click Retry.</p>
      )}
      {/*
        * The browser's own answer, not a guess. Firefox gates a public page's calls to 127.0.0.1 behind a
        * local-network permission, and telling those customers their Device Service was misconfigured sent
        * them to change settings on software that was working.
        */}
      {conn.status === 'permission' && (
        <div className="device-help">
          <p style={{ marginTop: 0 }}>
            <strong>This browser is blocking EFTCreator from reaching the Device Service on this computer.</strong>
          </p>
          <p>
            {conn.permState === 'denied'
              ? 'Local network access has been denied for this site.'
              : 'Local network access has not been allowed for this site yet.'}
          </p>
          {/*
            * The steps for the browser they are in. Chromium shipped this prompt in Chrome 142, so Chrome and
            * Edge users reach this screen too — and the Firefox wording sent them looking for a padlock control
            * their browser does not have.
            */}
          <ol className="device-steps">
            {permissionHelp(navigator.userAgent, conn.permState === 'denied' ? 'denied' : 'prompt').steps.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ol>
          <p style={{ marginBottom: 0 }}>
            Then <button className="linklike" onClick={() => void conn.connect()}>retry</button>. If a prompt
            appeared and you dismissed it, reloading the page asks again.
          </p>
        </div>
      )}
      {conn.status === 'insecure' && (
        <div className="device-help">
          <p style={{ marginTop: 0 }}>Couldn’t reach the Device Service. Make sure it’s running, then Retry.</p>
          <p style={{ marginBottom: 0 }}>If it is running, use <strong>Chrome or Edge</strong> — Safari/Firefox block an HTTPS page from calling the local service.</p>
        </div>
      )}
      {/*
        * Was: "It must return CORS + Access-Control-Allow-Private-Network: true for this origin." That named a
        * header from the retired Private Network Access design, which no current browser consults, and asked
        * the operator to change software they cannot edit — so the only use they had for it was to forward it
        * to support as a Device Service defect. It says what they can do, and where the real detail is.
        */}
      {conn.status === 'blocked' && (
        <div className="device-help">
          <p style={{ marginTop: 0 }}>
            <strong>The Device Service answered, but this page could not read its response.</strong>
          </p>
          <p style={{ marginBottom: 0 }}>
            Restart the Device Service and reload the page, then{' '}
            <button className="linklike" onClick={() => void conn.connect()}>retry</button>. If it keeps
            happening, open <strong>Help &gt; Device status</strong> — it shows the exact reason, which is
            what support needs.
          </p>
        </div>
      )}
      {connected && !serial && (
        <p className="device-help">Connected to the Device Service, but no scanner was detected. Plug in your scanner and click Rescan.</p>
      )}

      {connected && serial && (
        <div className="precheck" style={{ marginTop: 4 }}>
          {checking && <p className="device-help">Verifying your plan for this scanner…</p>}
          {!checking && check?.ok && (
            <div className="banner-ok">
              ✓ Ready to capture — {check.mode === 'licensed' ? 'this device is licensed for unlimited transactions' : 'billed per transaction (any device)'}.
            </div>
          )}
          {!checking && check && !check.ok && (
            <div className="banner-warn">
              <div style={{ marginBottom: 8 }}>{check.message ?? 'This scanner isn’t authorized for capture yet.'}</div>
              {check.code === 'DEVICE_SERVICE_OUTDATED' ? (
                /* Nothing about the plan or the scanner can help here — the fix is on this workstation. */
                <span>
                  Update the Device Service on this workstation to <strong>v{minVersion}</strong> or newer from the{' '}
                  <Link to="/app/downloads" className="track-link">Downloads</Link> page, restart it, then{' '}
                  <button className="linklike" onClick={recheck}>re-check</button>.
                </span>
              ) : (check.code === 'NO_DEVICE_LICENSE') ? (
                /* The precheck has just auto-registered this scanner, so a free seat can be put on it from
                   here — no trip to the Devices page and back to do what we can already see is needed. */
                <ScannerSeatPrompt serial={serial} required onAllocated={recheck}>
                  <span>This is an unlimited (per-device) plan — allocate a license to this scanner on the{' '}
                    <Link to="/app/devices" className="track-link">Devices</Link> page, then{' '}
                    <button className="linklike" onClick={recheck}>re-check</button>.</span>
                </ScannerSeatPrompt>
              ) : check.status === 402 || check.code === null ? (
                <span>Choose a plan on the <Link to="/app/subscriptions" className="track-link">Subscriptions</Link> page, then{' '}
                  <button className="linklike" onClick={recheck}>re-check</button>.</span>
              ) : (
                <button className="linklike" onClick={recheck}>Re-check</button>
              )}
            </div>
          )}
        </div>
      )}

      <div className="flow-actions">
        <Link to="/app/capture" className="btn btn-ghost">← Workflows</Link>
        <button className="btn btn-primary" disabled={!ready} onClick={onReady}>Continue →</button>
      </div>
    </div>
  );
}

// ---------------- Fingerprints ----------------
type Hand = 'left' | 'right';
type Digit = 'thumb' | 'index' | 'middle' | 'ring' | 'little';
interface Pos { scanType: string; label: string; hand: Hand; digits: Digit[]; motion: 'roll' | 'flat'; instruction: string }
/** `marked` = the operator flagged this position amputated/unprintable; the run carries on. */
type CapResult = 'captured' | 'failed' | 'cancelled' | 'marked';

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
function rolled(hand: Hand, digit: Digit): Pos {
  return {
    scanType: `ROLLED_${hand.toUpperCase()}_${digit.toUpperCase()}`,
    label: `${hand === 'right' ? 'R' : 'L'} ${cap(digit)}`,
    hand, digits: [digit], motion: 'roll',
    instruction: `Roll the ${hand} ${digit} on the scanner from one edge of the nail to the other.`,
  };
}
function flatFour(hand: Hand): Pos {
  return {
    scanType: `${hand.toUpperCase()}_FOUR`,
    label: `${hand === 'right' ? 'R' : 'L'} Four Fingers`,
    hand, digits: ['index', 'middle', 'ring', 'little'], motion: 'flat',
    instruction: `Place the ${hand.toUpperCase()} four fingers flat on the scanner and hold still.`,
  };
}
function flatThumb(hand: Hand): Pos {
  return {
    scanType: `FLAT_${hand.toUpperCase()}_THUMB`,
    label: `${hand === 'right' ? 'R' : 'L'} Thumb`,
    hand, digits: ['thumb'], motion: 'flat',
    instruction: `Place the ${hand.toUpperCase()} thumb flat on the scanner and hold still.`,
  };
}

/**
 * Half of a slap, for two-finger scanners whose platen can't take four fingers at once. The pair is
 * stitched by the Device Service into the slap the EBTS expects, so these scan types never reach the
 * EFT themselves — eftBuilder has no FGP for them and skips them.
 */
function halfSlap(hand: Hand, part: 'index_middle' | 'ring_little'): Pos {
  const digits: Digit[] = part === 'index_middle' ? ['index', 'middle'] : ['ring', 'little'];
  const words = part === 'index_middle' ? 'index & middle' : 'ring & little';
  return {
    scanType: `${hand.toUpperCase()}_${part.toUpperCase()}`,
    label: `${hand === 'right' ? 'R' : 'L'} ${part === 'index_middle' ? 'Index+Middle' : 'Ring+Little'}`,
    hand, digits, motion: 'flat',
    instruction: `Place the ${hand.toUpperCase()} ${words} fingers flat on the scanner and hold still.`,
  };
}

const ROLL_DIGITS: Digit[] = ['thumb', 'index', 'middle', 'ring', 'little'];
const ROLLED_RIGHT: Pos[] = ROLL_DIGITS.map((d) => rolled('right', d));
const ROLLED_LEFT: Pos[] = ROLL_DIGITS.map((d) => rolled('left', d));
const RIGHT_FOUR = flatFour('right');
const LEFT_FOUR = flatFour('left');
const RIGHT_FLAT_THUMB = flatThumb('right');
const LEFT_FLAT_THUMB = flatThumb('left');
const HALF_SLAPS: Record<Hand, [Pos, Pos]> = {
  right: [halfSlap('right', 'index_middle'), halfSlap('right', 'ring_little')],
  left: [halfSlap('left', 'index_middle'), halfSlap('left', 'ring_little')],
};
/** Slap scan type each pair of half-slaps stitches into. */
const SLAP_OF: Record<Hand, string> = { right: RIGHT_FOUR.scanType, left: LEFT_FOUR.scanType };

/**
 * Display order of a hand's two half-slaps inside the slap's own column: outer fingers outermost, the
 * way the hand lies on the platen, so the left pair reads ring+little then index+middle and the right
 * pair the reverse. Independent of CAPTURE_ORDER, which always takes index+middle first.
 */
const HALF_PAIR: Record<Hand, Pos[]> = {
  left: [HALF_SLAPS.left[1], HALF_SLAPS.left[0]],
  right: [HALF_SLAPS.right[0], HALF_SLAPS.right[1]],
};

const HANDS: Hand[] = ['left', 'right'];

/** The hand a four-finger slap belongs to, or null when the scan type isn't a slap. */
function handOfSlap(scanType: string): Hand | null {
  return HANDS.find((h) => SLAP_OF[h] === scanType) ?? null;
}

// Display grid — laid out like the FD-258 card (rolled right, rolled left, then the flat impressions).
const FD_ROW1: Pos[] = ROLLED_RIGHT;
const FD_ROW2: Pos[] = ROLLED_LEFT;
const FD_ROW3: Pos[] = [LEFT_FOUR, LEFT_FLAT_THUMB, RIGHT_FLAT_THUMB, RIGHT_FOUR];

/**
 * Order the scanner actually walks through, which is deliberately NOT the card layout above: per hand
 * it's the four-finger slap, the plain thumb, then the rolled thumb through little — right hand first,
 * then the left. Keyed by scanType everywhere, so the grid and the sequence stay independent.
 */
const CAPTURE_ORDER: Pos[] = [
  RIGHT_FOUR, RIGHT_FLAT_THUMB, ...ROLLED_RIGHT,
  LEFT_FOUR, LEFT_FLAT_THUMB, ...ROLLED_LEFT,
];

/**
 * The same sequence with each slap replaced by its two half-slaps, for a two-finger scanner. The slap
 * itself is never captured directly on those devices — it is produced by stitching the pair.
 */
const CAPTURE_ORDER_TWO_FINGER: Pos[] = CAPTURE_ORDER.flatMap((p) =>
  p.scanType === RIGHT_FOUR.scanType ? HALF_SLAPS.right
    : p.scanType === LEFT_FOUR.scanType ? HALF_SLAPS.left
    : [p],
);

/** Every position either order can visit, so lookups work regardless of the connected scanner. */
const POS_OF: Record<string, Pos> = Object.fromEntries(
  [...CAPTURE_ORDER, ...CAPTURE_ORDER_TWO_FINGER].map((p) => [p.scanType, p]),
);
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/** Stylized hand with the target digit(s) highlighted — right-hand geometry, mirrored for the left. */
function HandGuide({ hand, digits }: { hand: Hand; digits: Digit[] }) {
  const fill = (d: Digit) => (digits.includes(d) ? 'var(--accent)' : 'var(--surface-3)');
  const line = (d: Digit) => (digits.includes(d) ? 'var(--accent-bright)' : 'var(--border-strong)');
  return (
    <svg viewBox="0 0 132 150" width="112" height="128" aria-hidden style={hand === 'left' ? { transform: 'scaleX(-1)' } : undefined}>
      {/* palm */}
      <rect x="42" y="72" width="64" height="60" rx="24" fill="var(--surface-3)" stroke="var(--border-strong)" />
      {/* four fingers (index → little), middle tallest, rounded tips */}
      <rect x="48" y="30" width="13" height="52" rx="6.5" fill={fill('index')} stroke={line('index')} strokeWidth="1.5" />
      <rect x="64" y="18" width="13" height="64" rx="6.5" fill={fill('middle')} stroke={line('middle')} strokeWidth="1.5" />
      <rect x="80" y="26" width="13" height="56" rx="6.5" fill={fill('ring')} stroke={line('ring')} strokeWidth="1.5" />
      <rect x="95" y="40" width="12" height="42" rx="6" fill={fill('little')} stroke={line('little')} strokeWidth="1.5" />
      {/* thumb — vertical, set to the lower-left of the palm */}
      <rect x="20" y="70" width="15" height="52" rx="7.5" fill={fill('thumb')} stroke={line('thumb')} strokeWidth="1.5" />
    </svg>
  );
}

/** Capture dialog overlaid on the card grid: which finger to place, live preview, and status. */
function CaptureModal({ pos, preview, index, total, onStop, onMark, onManual, manualBusy }: {
  pos: Pos; preview: string | null; index: number; total: number;
  onStop: () => void; onMark: (code: AmpCode) => void;
  /** Force the Device Service to accept the current frame (v1's manual capture). */
  onManual: () => void;
  manualBusy?: boolean;
}) {
  return (
    <div className="fp-modal-backdrop" role="dialog" aria-modal="true">
      <div className="fp-modal">
        <div className="fp-modal-head">
          <div>
            <div className="fp-modal-eyebrow">Capturing · position {index} of {total}</div>
            <h3>{pos.label}</h3>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={onStop}>Stop</button>
        </div>
        <div className="fp-modal-body">
          <div className="fp-modal-guide">
            <HandGuide hand={pos.hand} digits={pos.digits} />
            <span className="fp-motion">{pos.motion === 'roll' ? 'Rolled impression' : 'Flat impression'}</span>
            <p className="fp-instruction">{pos.instruction}</p>
          </div>
          <div className="fp-modal-preview">
            {preview
              ? <img src={`data:image/png;base64,${preview}`} alt="capture preview" />
              : <div className="fp-preview-empty">Waiting for the scanner…</div>}
            {/*
              * One strip along the bottom so the status pill and the button can't overlap. The preview is a
              * single centred grid cell, so an in-flow button landed on top of the image — and behind the
              * absolutely-positioned pill, which paints above in-flow siblings.
              *
              * Manual capture exists because some scanners never decide the impression is good enough while
              * the operator can plainly see a usable print. v1 had it; without it the only way out is to
              * cancel and lose the capture.
              */}
            <div className="fp-preview-controls">
              <div className="fp-scanning"><span className="fp-pulse" /> Scanning…</div>
              <button className="btn btn-primary btn-sm" onClick={onManual} disabled={manualBusy}>
                {manualBusy ? 'Capturing…' : 'Manual capture'}
              </button>
            </div>
          </div>
        </div>
        <div className="fp-modal-foot">
          <span className="fp-modal-foot-hint">Can’t capture this finger?</span>
          <button className="btn btn-ghost btn-sm" onClick={() => onMark('XX')}>Amputated (XX)</button>
          <button className="btn btn-ghost btn-sm" onClick={() => onMark('UP')}>Bandaged / Unprintable (UP)</button>
        </div>
      </div>
    </div>
  );
}

/**
 * Review an already-captured impression full size, with the option to retake it.
 *
 * Clicking a captured card used to recapture on the spot, which meant a mis-click discarded a good print
 * and the subject had to be re-inked/re-scanned. Reviewing is the common intent; recapturing is one more
 * deliberate click.
 */
function ReviewModal({ pos, src, score, fullCacheId, fullSlot, onClose, onRecapture }: {
  // A complete image src, not raw base64: an imported impression is WSQ and is shown via the server's
  // rendering of it, so the caller decides what can actually be drawn.
  pos: Pos; src: string; score: number | null;
  /**
   * Where to fetch a full-resolution rendering, when `src` is only a stand-in. Reviewing a print is
   * exactly where a 220px thumbnail is not good enough — the operator is looking for ridge detail — but
   * it is half a megabyte, so it is fetched on open rather than with the import.
   *
   * Passed as two strings rather than a callback ON PURPOSE. A callback is a new value on every render,
   * and the effect below depends on it: each fetch sets state, which re-renders, which rebuilds the
   * callback, which fetches again. Primitives make the dependency stable.
   */
  fullCacheId?: string; fullSlot?: string;
  onClose: () => void; onRecapture: () => void;
}) {
  const [full, setFull] = useState<string | null>(null);
  useEffect(() => {
    setFull(null);
    if (!fullCacheId || !fullSlot) return;
    let cancelled = false;
    // Falls back to the stand-in on failure: a slow or failed fetch must not leave an empty dialog
    // where the operator expected their print.
    void fullImage(fullCacheId, fullSlot)
      .then((r) => { if (!cancelled) setFull(r.png); })
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, [fullCacheId, fullSlot]);
  return (
    <div className="fp-modal-backdrop" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="fp-modal" onClick={(e) => e.stopPropagation()}>
        <div className="fp-modal-head">
          <div>
            <div className="fp-modal-eyebrow">Captured impression</div>
            <h3>{pos.label}</h3>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>Close</button>
        </div>
        <div className="fp-modal-body">
          <div className="fp-modal-guide">
            <HandGuide hand={pos.hand} digits={pos.digits} />
            <span className="fp-motion">{pos.motion === 'roll' ? 'Rolled impression' : 'Flat impression'}</span>
            {typeof score === 'number' ? (
              <p className="fp-instruction">
                NFIQ image quality <strong className={`nfiq-text nfiq-${qualityBand(score)}`}>{score}</strong> of 100.
                {qualityBand(score) === 'poor' && ' Consider retaking this impression.'}
              </p>
            ) : (
              <p className="fp-instruction">Image quality was not reported for this impression.</p>
            )}
          </div>
          <div className="fp-modal-preview">
            <img src={full ?? src} alt={`${pos.label} captured impression`} />
          </div>
        </div>
        <div className="fp-modal-foot">
          <span className="fp-modal-foot-hint">Not happy with this impression?</span>
          <button className="btn btn-primary btn-sm" onClick={onRecapture}>Recapture</button>
        </div>
      </div>
    </div>
  );
}

function Fingerprints({ conn, prints, setPrints, printThumbs, cacheId, ampCodes, setAmpCodes, quality, setQuality, positions, onBack, onNext }: {
  conn: DeviceConn;
  prints: Record<string, string>; setPrints: (fn: (p: Record<string, string>) => Record<string, string>) => void;
  /**
   * scanType -> a stand-in picture for impressions whose `prints` bytes the browser cannot draw
   * (imported WSQ), bound to the bytes it depicts.
   */
  printThumbs: Record<string, { src: string; forData: string; slot: string }>;
  /** The Ani cache holding the staged imagery, when one exists. */
  cacheId: string | null;
  ampCodes: Record<string, AmpCode>; setAmpCodes: (fn: (a: Record<string, AmpCode>) => Record<string, AmpCode>) => void;
  quality: Record<string, number | null>;
  setQuality: (fn: (q: Record<string, number | null>) => Record<string, number | null>) => void;
  /** EBTS positions this workflow collects. Absent means all fourteen. */
  positions: number[] | undefined;
  onBack: () => void; onNext: () => void;
}) {
  const svc = conn.svc;
  const connected = conn.status === 'connected' && Boolean(conn.device);
  const [capturing, setCapturing] = useState<string | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [running, setRunning] = useState(false);
  const [ampMenu, setAmpMenu] = useState<string | null>(null);
  const cancelRef = useRef(false);
  // Abort only the position being scanned (marking it amputated/unprintable) without ending the run —
  // distinct from cancelRef, which stops the whole sequence.
  const skipRef = useRef(false);
  // Last accepted capture. The Device Service's /device/finger/image holds the previous position's
  // finished frame until a new scan lands, so the first poll after startCapture can return that stale
  // frame with complete=true — which would copy one image onto every card (esp. during Capture all).
  // We reject a complete frame identical to this and keep polling for a genuinely new one.
  const lastImageRef = useRef<string | null>(null);
  /** Hand currently being stitched, so the UI can say so instead of looking idle. */
  const [stitching, setStitching] = useState<Hand | null>(null);
  const [stitchErr, setStitchErr] = useState<string | null>(null);
  const [manualBusy, setManualBusy] = useState(false);
  /** Scanner DPI per captured position — the stitch DTO carries it onto the composited slap. */
  const dpiRef = useRef<Record<string, number>>({});
  /** Position whose captured image is open for review, or null. */
  const [reviewing, setReviewing] = useState<string | null>(null);

  const printsRef = useRef(prints);
  printsRef.current = prints;
  const qualityRef = useRef(quality);
  qualityRef.current = quality;

  /*
   * A two-finger scanner can't take a four-finger slap, so it walks the order with each slap replaced by
   * two half-slaps and the slap produced by stitching them. Derived from the connected device rather
   * than configured: swapping scanners mid-session picks up the right sequence.
   */
  const twoFinger = isTwoFingerScanner(conn.device?.deviceName);
  /*
   * Narrowed to the positions this workflow asks for.
   *
   * A half-slap is kept whenever its slap is wanted: the halves are how a two-finger scanner has to take
   * that impression, not impressions in their own right — the builder has no FGP for them and drops them.
   * Filtering them out by FGP would leave such a scanner unable to produce a slap it was asked for.
   */
  const wanted = new Set(requiredPositions(positions));
  const inWorkflow = (p: Pos): boolean => {
    const fgp = FGP[p.scanType];
    if (fgp) return wanted.has(fgp);
    const hand = HANDS.find((h) => HALF_SLAPS[h].some((x) => x.scanType === p.scanType));
    return hand ? wanted.has(FGP[SLAP_OF[hand]]!) : true;
  };
  const captureOrder = (twoFinger ? CAPTURE_ORDER_TWO_FINGER : CAPTURE_ORDER).filter(inWorkflow);
  const row1 = FD_ROW1.filter(inWorkflow);
  const row2 = FD_ROW2.filter(inWorkflow);
  const row3 = FD_ROW3.filter(inWorkflow);

  /**
   * Which image a hand's slap column shows on a two-finger scanner: the two half-slaps that were actually
   * captured, or the four-finger slap stitched from them. They share one cell of the FD-258 layout because
   * they are the same impression — the halves are how this hardware has to take it, the slap is what the
   * EBTS receives — so the column flips between them instead of the card growing an extra row.
   *
   * Starts on the halves, which is what the operator captures and reviews, and flips to the slap when one
   * appears.
   */
  const [slapView, setSlapView] = useState<Record<Hand, 'halves' | 'slap'>>({ left: 'halves', right: 'halves' });
  /**
   * Flip on the EDGE of a slap appearing or being invalidated, not on its presence: presence would
   * re-assert the slap view on every later capture and undo an operator who deliberately flipped back to
   * look at the halves.
   */
  const slapSeenRef = useRef<Record<Hand, boolean>>({ left: false, right: false });
  useEffect(() => {
    for (const h of HANDS) {
      const has = Boolean(prints[SLAP_OF[h]]);
      if (has === slapSeenRef.current[h]) continue;
      slapSeenRef.current[h] = has;
      setSlapView((v) => ({ ...v, [h]: has ? 'slap' : 'halves' }));
    }
  }, [prints]);

  const resolved = (st: string) => Boolean(prints[st]) || Boolean(ampCodes[st]);

  /**
   * Once both halves of a hand are in, ask the Device Service to composite them into the slap the EBTS
   * expects. Writes the result under the SLAP scan type (RIGHT_FOUR / LEFT_FOUR) — the halves stay in
   * `prints` for operator review but never reach the EFT, since eftBuilder has no FGP for them.
   *
   * Called with the freshly captured image passed in rather than read from state: setPrints is async, so
   * reading `prints` straight after capturing the second half would still see it missing.
   */
  /**
   * Discard a stitched slap. Its inputs are the two half-slaps, so the moment either one changes the
   * existing composite describes fingers that are no longer there — and the slap, not the halves, is what
   * reaches the EBTS. Better an empty slap card the operator can see than a silently stale image.
   */
  function invalidateSlap(hand: Hand) {
    const slap = SLAP_OF[hand];
    setPrints((p) => { const n = { ...p }; delete n[slap]; return n; });
    setQuality((q) => { const n = { ...q }; delete n[slap]; return n; });
  }

  async function stitchIfComplete(
    hand: Hand,
    justCaptured: Record<string, string>,
    justScored: Record<string, number | null> = {},
  ) {
    const [im, rl] = HALF_SLAPS[hand];
    const imData = justCaptured[im.scanType] ?? printsRef.current[im.scanType];
    const rlData = justCaptured[rl.scanType] ?? printsRef.current[rl.scanType];
    // Whatever slap exists was built from the previous pair, so it is stale from here on. Drop it first
    // and let a successful stitch write the replacement — that way a failed or empty stitch leaves the
    // slap visibly missing instead of keeping an image that no longer matches its halves.
    invalidateSlap(hand);
    if (!svc || !imData || !rlData) return;
    setStitching(hand);
    setStitchErr(null);
    try {
      const out = await svc.stitchSlap(
        hand === 'right' ? 'RIGHT_HAND' : 'LEFT_HAND',
        { data: imData, dpi: dpiRef.current[im.scanType] },
        { data: rlData, dpi: dpiRef.current[rl.scanType] },
      );
      if (out?.data) {
        const slap = SLAP_OF[hand];
        setPrints((p) => ({ ...p, [slap]: out.data as string }));
        setAmpCodes((a) => { const n = { ...a }; delete n[slap]; return n; });
        // The stitched image isn't re-assessed by the scanner, so carry the halves' mean across.
        const score = combineQuality(
          justScored[im.scanType] ?? qualityRef.current[im.scanType],
          justScored[rl.scanType] ?? qualityRef.current[rl.scanType],
        );
        setQuality((q) => ({ ...q, [slap]: score }));
      } else {
        setStitchErr(`The ${hand} hand's two captures did not combine into a four-finger slap. Recapture either half to retry.`);
      }
    } catch {
      // Non-fatal: the halves are kept, so the operator can retry the stitch by recapturing either one.
      setStitchErr(`Could not combine the ${hand} hand's two captures into a four-finger slap. Recapture either half to retry.`);
    } finally {
      setStitching(null);
    }
  }

  /** The hand a half-slap belongs to, or null when the scan type isn't a half-slap. */
  function handOfHalf(scanType: string): Hand | null {
    return (['left', 'right'] as Hand[]).find((h) => HALF_SLAPS[h].some((p) => p.scanType === scanType)) ?? null;
  }

  /**
   * Force the in-progress capture to complete. The polling loop in runCapture then sees complete=true on
   * its next poll and stores the frame exactly as it would an auto-capture — including the NFIQ read and,
   * for a half-slap, the stitch — so nothing about the sequence needs to know this happened.
   */
  async function manualCapture() {
    if (!svc || !capturing) return;
    setManualBusy(true);
    try {
      await svc.forceCapture();
    } catch {
      setStitchErr('The scanner did not accept a manual capture. Try again, or stop and recapture.');
    } finally {
      setManualBusy(false);
    }
  }

  /** Capture one position, streaming preview frames. Retries transient poll errors so a single
   *  hiccup doesn't abort a Capture-All run. Returns captured / failed / cancelled. */
  async function runCapture(scanType: string): Promise<CapResult> {
    if (!svc) return 'failed';
    setCapturing(scanType);
    setPreview(null);
    try {
      // Clear any residual/completed frame from the previous position first — a bare startCapture can
      // leave it in the device buffer, which stalls the next position (why a manual stop→start "fixes"
      // it). Resetting here is what lets the sequence auto-advance print-to-print.
      await svc.cancelCapture().catch(() => undefined);
      await svc.startCapture(scanType);
      let pollErrs = 0;
      for (let i = 0; i < 300; i++) {
        if (cancelRef.current) { await svc.cancelCapture().catch(() => undefined); return 'cancelled'; }
        if (skipRef.current) {
          skipRef.current = false;
          await svc.cancelCapture().catch(() => undefined);
          return 'marked';
        }
        let img;
        try { img = await svc.pollImage(); pollErrs = 0; }
        catch { if (++pollErrs > 6) return 'failed'; await sleep(250); continue; }
        /*
         * Re-check AFTER the await. The operator can mark this position, or stop the run, while the poll is
         * in flight — and the frame that then arrives belongs to a position they have just called
         * amputated. Storing it would overwrite the mark with an image, which `markAmp` had already
         * deleted, leaving a print for a finger the submission says is not there.
         */
        if (skipRef.current) { skipRef.current = false; await svc.cancelCapture().catch(() => undefined); return 'marked'; }
        if (cancelRef.current) { await svc.cancelCapture().catch(() => undefined); return 'cancelled'; }
        // A frame identical to the last accepted capture is the previous position's stale carry-over —
        // don't preview it, and don't let it complete this position (that's the duplication bug).
        const stale = Boolean(img?.data) && img?.data === lastImageRef.current;
        if (img?.data && !stale) setPreview(img.data);
        if (img?.complete && !(img.data && stale)) {
          if (img.data) {
            const data = img.data;
            lastImageRef.current = data;
            setPrints((p) => ({ ...p, [scanType]: data }));
            setAmpCodes((a) => { const n = { ...a }; delete n[scanType]; return n; });
            // Advisory NFIQ for the card badge. Never gates the capture: a failed or missing reading
            // just leaves the position unscored rather than interrupting the run.
            if (img.dpi) dpiRef.current[scanType] = img.dpi;
            let score: number | null = null;
            try { score = meanQuality(await svc.detectFingers()); } catch { /* unscored */ }
            setQuality((q) => ({ ...q, [scanType]: score }));
            // Recapturing a half must rebuild the slap, not just replace the half.
            const halfHand = handOfHalf(scanType);
            if (halfHand) await stitchIfComplete(halfHand, { [scanType]: data }, { [scanType]: score });
          }
          return 'captured';
        }
        await sleep(200);
      }
      return 'failed';
    } catch {
      return 'failed';
    } finally {
      setCapturing(null);
    }
  }

  /** Capture from `startIdx` onward: (re)capture the starting position, then AUTO-ADVANCE through each
   *  subsequent unresolved position — so one print flows straight into the next with no stop/restart.
   *  A failed/timed-out position is skipped (not fatal), and so is one marked amputated/unprintable;
   *  only an explicit Stop ends the run. */
  async function runFrom(startIdx: number, force?: Set<string>) {
    if (startIdx < 0) return;
    setRunning(true);
    skipRef.current = false;
    try {
      for (let i = startIdx; i < captureOrder.length; i++) {
        if (cancelRef.current) break;
        const p = captureOrder[i]!;
        // `force` exists because `resolved` closes over this render's `prints`: clearing a position and
        // calling runFrom in the same tick would still see it as captured and skip it.
        if (i !== startIdx && resolved(p.scanType) && !force?.has(p.scanType)) continue;
        const r = await runCapture(p.scanType);
        if (r === 'cancelled') break;
        await sleep(350); // let the scanner reset before the next position
      }
    } finally {
      setRunning(false);
    }
  }

  /** Click a card: capture it, then continue automatically through the remaining uncaptured positions. */
  function captureOne(scanType: string) {
    if (running || capturing) return;
    cancelRef.current = false;
    // On a two-finger scanner the slap cards can't be captured directly — the hardware can't take four
    // fingers. Clicking one retakes that hand's pair, which is what the operator meant.
    if (twoFinger) {
      const hand = (Object.keys(SLAP_OF) as Hand[]).find((h) => SLAP_OF[h] === scanType);
      if (hand) { recaptureSlap(hand); return; }
    }
    void runFrom(captureOrder.findIndex((p) => p.scanType === scanType));
  }

  /**
   * Retake a hand's four-finger slap on a two-finger scanner, which means retaking BOTH halves.
   *
   * A slap is only as good as the pair it was stitched from, so recapturing one half and keeping the other
   * would produce a composite of one fresh and one old impression. Both halves and the slap are cleared,
   * and `force` makes the run retake them even though this render still sees them as captured.
   */
  function recaptureSlap(hand: Hand) {
    const [im, rl] = HALF_SLAPS[hand];
    const both = [im.scanType, rl.scanType];
    setPrints((p) => { const n = { ...p }; for (const st of [...both, SLAP_OF[hand]]) delete n[st]; return n; });
    setAmpCodes((a) => { const n = { ...a }; for (const st of both) delete n[st]; return n; });
    setQuality((q) => { const n = { ...q }; for (const st of [...both, SLAP_OF[hand]]) delete n[st]; return n; });
    setStitchErr(null);
    void runFrom(captureOrder.findIndex((p) => p.scanType === im.scanType), new Set(both));
  }

  /** Capture all: start at the first uncaptured position and auto-advance through the rest. */
  function captureAll() {
    if (running || capturing) return;
    cancelRef.current = false;
    void runFrom(captureOrder.findIndex((p) => !resolved(p.scanType)));
  }

  function stop() {
    cancelRef.current = true;
    setRunning(false);
    /*
     * Cancel on the device too, for the same reason as `markCurrent`: the loop only re-reads `cancelRef`
     * between polls, so Stop did nothing at all while a poll was outstanding — the modal stayed up and the
     * grid stayed disabled. Ending the capture at the scanner is what makes the pending poll settle.
     */
    void svc?.cancelCapture().catch(() => undefined);
  }

  // The score belongs to an image; dropping the image must drop the score, or a cleared card would keep
  // showing a badge for a print that is no longer there.
  const dropScore = (scanType: string) =>
    setQuality((q) => { const n = { ...q }; delete n[scanType]; return n; });

  function markAmp(scanType: string, code: AmpCode) {
    /*
     * Marking a digit clears every impression that shows only that digit — the rolled finger and, for a
     * thumb, its flat impression too. An AMP entry and a picture of the same finger contradict each other,
     * and the operator should see the card reflect that rather than discover it at build time.
     *
     * A slap survives one marked finger: it depicts four, and the other three were taken properly.
     */
    const next = { ...ampCodes, [scanType]: code };
    const clear = imagesToClear(next);
    setAmpCodes(() => next);
    setPrints((p) => { const n = { ...p }; for (const st of clear) delete n[st]; delete n[scanType]; return n; });
    setQuality((q) => { const n = { ...q }; for (const st of clear) delete n[st]; return n; });
    dropScore(scanType);
    // Losing a half leaves the stitched slap describing a finger that is no longer captured.
    const hand = handOfHalf(scanType);
    if (hand) invalidateSlap(hand);
    setAmpMenu(null);
  }
  function clearMark(scanType: string) {
    setAmpCodes((a) => { const n = { ...a }; delete n[scanType]; return n; });
    setPrints((p) => { const n = { ...p }; delete n[scanType]; return n; });
    dropScore(scanType);
    const hand = handOfHalf(scanType);
    if (hand) invalidateSlap(hand);
    setAmpMenu(null);
  }
  /**
   * Mark the position currently being captured (from the capture dialog). Only this position's scan is
   * abandoned — the run continues with the remaining prints, which is the point: previously this set
   * cancelRef and ended a Capture-All run at the first amputated or bandaged finger.
   */
  function markCurrent(code: AmpCode) {
    const st = capturing;
    if (!st) return;
    skipRef.current = true;
    /*
     * Cancel on the DEVICE immediately rather than waiting for the poll loop to notice the flag.
     *
     * The loop only re-reads `skipRef` at the top of an iteration, so a poll that is still in flight has to
     * settle first. When the scanner is waiting for a finger that will never be presented — which is
     * precisely the case here — that poll could hang, and `capturing` stayed set. Every card and every mark
     * button on the grid is disabled while it is, so marking one finger amputated locked the operator out
     * of capturing any of the others with no way back short of reloading the page.
     */
    void svc?.cancelCapture().catch(() => undefined);
    markAmp(st, code);
  }

  const resolvedCount = captureOrder.filter((p) => resolved(p.scanType)).length;
  /** Required positions with neither an image nor an amputated/unprintable mark. */
  const outstanding = missingPositions(positions, prints, ampCodes);
  const activePos = capturing ? POS_OF[capturing] : undefined;
  const activeIndex = capturing ? captureOrder.findIndex((p) => p.scanType === capturing) + 1 : 0;

  /*
   * What to DRAW for a position. A captured print is PNG and renders straight from `prints`; an
   * imported one is WSQ, which no browser decodes, so the server's thumbnail stands in. Returns a
   * complete src either way so callers never build one themselves and miss a case.
   */
  const imageSrc = (scanType: string): string | undefined => {
    const raw = prints[scanType];
    if (!raw) return undefined;
    /*
     * The stand-in is used only while it still depicts what `prints` holds. Re-capturing over an
     * imported impression replaces those bytes, which invalidates it here without anything having to
     * remember to clear it — and forgetting to clear it would leave the operator looking at the print
     * they just replaced.
     */
    const thumb = printThumbs[scanType];
    if (thumb && thumb.forData === raw) return thumb.src;
    return `data:image/png;base64,${raw}`;
  };

  const cell = (p: Pos) => {
    const amp = ampCodes[p.scanType];
    const captured = prints[p.scanType];
    const score = quality[p.scanType];
    return (
      <div className="print-cell-wrap" key={p.scanType}>
        <button
          className={`print-cell${captured ? ' captured' : ''}${capturing === p.scanType ? ' capturing' : ''}${amp ? ' amp' : ''}`}
          disabled={!connected || running || capturing !== null || Boolean(amp)}
          // A captured position opens for review rather than immediately recapturing it: overwriting a
          // good print on a stray click meant re-taking it from the subject.
          onClick={() => (captured ? setReviewing(p.scanType) : captureOne(p.scanType))}
          title={amp ? (amp === 'XX' ? 'Amputated' : 'Bandaged/Unprintable') : captured ? 'Review image' : 'Capture'}
        >
          {amp
            ? <span className="amp-badge">{amp === 'XX' ? 'AMP' : 'UP'}</span>
            : captured
              ? <img src={imageSrc(p.scanType)} alt={p.label} />
              : <span className="print-plus">{capturing === p.scanType ? '⋯' : '+'}</span>}
          <span className="print-label">{p.label}</span>
          {/* Advisory only — a poor score never blocks the capture. */}
          {captured && typeof score === 'number' && (
            <span className={`nfiq-badge nfiq-${qualityBand(score)}`} title={`NFIQ image quality ${score} of 100`}>
              {score}
            </span>
          )}
        </button>
        <button className="print-mark" disabled={running || capturing !== null} aria-label={`Mark ${p.label}`}
          onClick={() => setAmpMenu((m) => (m === p.scanType ? null : p.scanType))}>⋯</button>
        {ampMenu === p.scanType && (
          <div className="amp-menu">
            <button onClick={() => markAmp(p.scanType, 'XX')}>Amputated (XX)</button>
            <button onClick={() => markAmp(p.scanType, 'UP')}>Bandaged / Unprintable (UP)</button>
            {(amp || captured) && <button onClick={() => clearMark(p.scanType)}>Clear</button>}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="panel fp-panel">
      {!connected && (
        <p className="device-help">Waiting for the scanner. If it doesn’t connect, go{' '}
          <button className="linklike" onClick={onBack}>back</button> to the Scanner step to troubleshoot.</p>
      )}

      {connected && (
        <div className="fp-capture">
          <div className="fp-toolbar">
            <div className="flow-hint">{resolvedCount} of {captureOrder.length} complete · Capture all (or click any card) captures each print and auto-advances to the next · ⋯ marks amputated / bandaged-unprintable</div>
            {running || capturing ? (
              <button className="btn btn-ghost btn-sm" onClick={stop}>Stop</button>
            ) : (
              <>
                <button className="btn btn-primary btn-sm" onClick={() => void captureAll()} disabled={resolvedCount === captureOrder.length}>Capture all</button>
                {resolvedCount > 0 && (
                  <button className="btn btn-ghost btn-sm" onClick={() => { setPrints(() => ({})); setAmpCodes(() => ({})); setQuality(() => ({})); setPreview(null); lastImageRef.current = null; }}>Clear</button>
                )}
              </>
            )}
          </div>

          {twoFinger && (
            <p className="device-help">
              <strong>{conn.device?.deviceName}</strong> captures two fingers at a time, so each hand is
              taken as index+middle then ring+little and combined into the four-finger slap automatically.
              Use <strong>Two-finger / Four-finger</strong> over each slap to switch between the halves you
              captured and the combined image.
              {stitching && <> Combining the {stitching} hand…</>}
            </p>
          )}
          {stitchErr && <p className="cart-error">{stitchErr}</p>}

          {/* A seat can be freed by an administrator, or a different scanner attached, after the entitlement
              check that let this step open — and the failure would otherwise surface only at submit, after
              the prints were taken. Renders nothing when the scanner is licensed or the plan is metered. */}
          <ScannerSeatPrompt serial={conn.device?.deviceSerial} />

          <div className="fd258">
            {/*
              * The card shows only what this workflow collects, and a row with nothing left in it is not
              * rendered at all — a thumbs-only workflow should not present two empty rows of rolled
              * fingers for an operator to wonder about.
              */}
            {row1.length > 0 && <>
              <div className="fd-rowlabel">Rolled impressions — right hand</div>
              <div className="fd-row">{row1.map(cell)}</div>
            </>}
            {row2.length > 0 && <>
              <div className="fd-rowlabel">Rolled impressions — left hand</div>
              <div className="fd-row">{row2.map(cell)}</div>
            </>}
            {row3.length > 0 && <div className="fd-rowlabel">Plain impressions — slaps &amp; thumbs</div>}
            {/*
              * On a two-finger scanner each slap column shows either the pair of half-slaps or the slap
              * stitched from them, so the switch sits in its own row on the same 2fr 1fr 1fr 2fr grid.
              * Keeping it out of the column means the four image cells stay exactly the same height
              * whichever view is showing; the two thumb columns are left empty.
              */}
            {twoFinger && (
              <div className="fd-row3 fd-flip-row">
                {row3.map((p) => {
                  const hand = handOfSlap(p.scanType);
                  if (!hand) return <div key={p.scanType} aria-hidden />;
                  const stitched = Boolean(prints[SLAP_OF[hand]]);
                  const view = slapView[hand];
                  return (
                    <div className="seg seg-xs" key={p.scanType} role="group" aria-label={`${p.label} view`}>
                      <button className={`seg-btn${view === 'halves' ? ' is-active' : ''}`}
                        onClick={() => setSlapView((v) => ({ ...v, [hand]: 'halves' }))}>
                        Two-finger
                      </button>
                      <button className={`seg-btn${view === 'slap' ? ' is-active' : ''}`}
                        disabled={!stitched}
                        title={stitched ? 'Show the four-finger slap stitched from both halves' : 'Capture both halves to produce the four-finger slap'}
                        onClick={() => setSlapView((v) => ({ ...v, [hand]: 'slap' }))}>
                        {stitching === hand ? 'Combining…' : 'Four-finger'}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
            <div className="fd-row fd-row3">
              {row3.map((p) => {
                const hand = twoFinger ? handOfSlap(p.scanType) : null;
                if (!hand || slapView[hand] === 'slap') return cell(p);
                return <div className="fd-half-pair" key={p.scanType}>{HALF_PAIR[hand].map(cell)}</div>;
              })}
            </div>
          </div>
        </div>
      )}

      <div className="flow-actions">
        <button className="btn btn-ghost" onClick={onBack} disabled={running}>← Back</button>
        {/*
          * Every required position captured or accounted for. Marking a finger amputated or unprintable
          * RESOLVES it — a missing finger cannot be captured, and the submission records that instead.
          * Blocked here rather than at submit so the operator is told while the subject is still present.
          */}
        {outstanding.length > 0 && (
          <span className="fp-outstanding">Still needed: {describePositions(outstanding)}</span>
        )}
        <button className="btn btn-primary" onClick={onNext} disabled={outstanding.length > 0 || running}>Continue →</button>
      </div>

      {reviewing && POS_OF[reviewing] && (
        <ReviewModal
          pos={POS_OF[reviewing]!}
          src={imageSrc(reviewing)!}
          score={quality[reviewing] ?? null}
          {...(printThumbs[reviewing] && cacheId
            ? { fullCacheId: cacheId, fullSlot: printThumbs[reviewing]!.slot }
            : {})}
          onClose={() => setReviewing(null)}
          onRecapture={() => { const st = reviewing; setReviewing(null); captureOne(st); }}
        />
      )}
      {activePos && <CaptureModal pos={activePos} preview={preview} index={activeIndex} total={captureOrder.length} onStop={stop} onMark={markCurrent}
        onManual={() => void manualCapture()} manualBusy={manualBusy} />}
    </div>
  );
}

// ---------------- Documents (card conversion) ----------------
function Documents({ docKeys, setDocKeys, onBack, onNext }: {
  docKeys: string[]; setDocKeys: (fn: (k: string[]) => string[]) => void; onBack: () => void; onNext: () => void;
}) {
  const [uploading, setUploading] = useState(false);
  /** What was accepted, with its preview — the operator should see the card, not just a filename. */
  const [items, setItems] = useState<{ name: string; thumbnail: string; width: number; height: number }[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busyNote, setBusyNote] = useState<string | null>(null);

  /*
   * Everything is prepared in the browser BEFORE it is uploaded.
   *
   * A PDF is rasterised to PNG at 500 DPI (the segmenter reads images, not PDFs — a PDF upload used to fail
   * three seconds after submission and go to manual review), and every resulting image is measured. Rejecting a
   * too-small scan here costs the customer ten seconds; discovering it after submission costs them a day and a
   * support ticket.
   */
  async function onFiles(files: FileList | null) {
    if (!files?.length) return;
    setUploading(true);
    setError(null);
    try {
      for (const file of Array.from(files)) {
        const isPdf = file.type === 'application/pdf' || /\.pdf$/i.test(file.name);
        setBusyNote(isPdf ? `Converting ${file.name} to images…` : `Checking ${file.name}…`);
        const prepared = await prepareCardImages(file);

        for (const image of prepared) {
          setBusyNote(`Uploading ${image.file.name}…`);
          const { url, key } = await api.post<{ url: string; key: string }>('/uploads/presign', {
            contentType: 'image/png', ext: 'png',
          });
          const put = await fetch(url, { method: 'PUT', headers: { 'content-type': image.file.type }, body: image.file });
          if (!put.ok) throw new Error(`Upload failed for ${image.file.name}`);
          setDocKeys((k) => [...k, key]);
          setItems((n) => [...n, { name: image.file.name, thumbnail: image.thumbnail, width: image.width, height: image.height }]);
        }
      }
    } catch (e) {
      // A CardImageError already reads as advice; anything else is a genuine upload failure.
      setError(e instanceof Error ? e.message : 'Upload failed');
    } finally {
      setUploading(false);
      setBusyNote(null);
    }
  }

  return (
    <div className="panel">
      <div className="banner-warn" style={{ marginBottom: 14 }}>
        <strong>Scan at 500 DPI or higher.</strong> Fingerprint cards must be scanned at a minimum of 500 DPI —
        most scanners do not offer 500 exactly, so choose the next one up, usually 600. A card scanned lower
        cannot be converted. PDFs are fine: we convert them for you.
      </div>
      <label className="dropzone">
        <input type="file" accept="image/tiff,image/jpeg,image/png,application/pdf" multiple hidden
          disabled={uploading} onChange={(e) => onFiles(e.target.files)} />
        <span className="dz-icon">⭳</span>
        <span>{busyNote ?? (uploading ? 'Working…' : 'Click to upload scanned fingerprint card image(s)')}</span>
        <span className="dz-sub">TIFF, JPEG, PNG or PDF · minimum 8″ × 8″ at 500 DPI</span>
      </label>
      {error && <p className="cart-error">{error}</p>}
      {items.length > 0 && (
        <div className="doc-thumbs">
          {items.map((it, i) => (
            <figure key={i} className="doc-thumb">
              <img src={it.thumbnail} alt={it.name} />
              <figcaption>
                <span className="doc-thumb-name">{it.name}</span>
                <span className="doc-thumb-dim">{it.width}×{it.height}</span>
              </figcaption>
            </figure>
          ))}
        </div>
      )}
      <div className="flow-actions">
        <button className="btn btn-ghost" onClick={onBack}>← Back</button>
        <span className="flow-hint">{docKeys.length} image(s) uploaded</span>
        <button className="btn btn-primary" onClick={onNext} disabled={docKeys.length === 0}>Continue →</button>
      </div>
    </div>
  );
}
